/**
 * NewProducer Component
 * 
 * This component handles three distinct modes of operation for wine management:
 * 1. New Producer Mode: Create new producer with wine
 * 2. Add Wine Mode: Add wine to existing producer
 * 3. Edit Wine Mode: Modify existing wine details
 * 
 * UNIFIED EDIT MODE BEHAVIOR:
 * - Edit Wine Mode works identically for both direct input and external input sources
 * - Keywords are NOT auto-generated on form mount in edit mode
 * - Form fields are populated from fetched API data
 * - Keywords are only regenerated when user clicks the "Regenerate Keywords" button
 * - Producer keyword is preserved from API data and not overwritten
 * 
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

// Context and API Hooks
import { useWineContext } from './useWineContext';
import { useExternalInputContext } from './ExternalInputContext';
import { useCountryApi } from '../../store/api/hooks/countryApiHook';
import { useRegionApi } from '../../store/api/hooks/regionApiHook';
import { useSubRegionApi } from '../../store/api/hooks/subRegionApiHook';
import { useAppellationApi } from '../../store/api/hooks/appellationApiHook';
import { useSubAppellationApi } from '../../store/api/hooks/subAppellationApiHook';
// import { useLocalesApi } from '../../store/api/hooks/localesApiHook';
import { useDirectInputApi } from '../../store/api/hooks/directInputApiHook';

// UI Components
import InputGroup from "../../components/FormElements/InputGroup";
import CreatableMultiSelectInputGroup from "../../components/FormElements/CreatableMultiSelectInputGroup";
import BreadCrumb from '../../components/BreadCrumb';
import WineInfiniteTable from '../../components/WineAdditionComponents/WineInfiniteTable';
import Button from '../../components/Buttons';
import MergeWinesModal from './MergeWinesModal';
import Badge from '../../components/Badge';
import { Input } from '../../components/input';

// Icons and Utilities
import { Merge } from 'lucide-react';
import toast from 'react-hot-toast';
import SearchableFloatingSelect from '../../components/FormElements/SearchableFloatingSelect';
import ExternalWineInfoTable from './ExternalWineInfoTable';
import ValidationWineInfoTable from './ValidationWineInfoTable';
import Counter from '../../components/Counter';
import TooltipButton from '../../components/Tooltip';
import { set } from 'react-hook-form';
import { useNavigationWarning, navigationWarningManager } from '../../hooks/useNavigationWarning';
/**
 * Component Modes:
 * 1. New Producer Mode (default)
 *    - Triggered by: clicking "+ wine producer"
 *    - Shows producer name and producer fields
 *    - Does not show WineInfiniteTable
 * 
 * 2. Add Wine to Existing Producer Mode
 *    - Triggered by: clicking "+wine" on producer table
 *    - Hides producer name and producer fields
 *    - Shows WineInfiniteTable for existing wines
 * 
 * 3. Edit Wine Mode
 *    - Triggered by: clicking edit icon on wine table
 *    - Populates form with existing wine data
 *    - Shows WineInfiniteTable for existing wines
 */

// =============================================================================
// INTERFACES AND TYPES
// =============================================================================
// Add type for keyword weights
type KeywordWeights = {
    producerKeyword: number;
    baseKeyword: number;
    appellationKeyword: number;
    colorKeyword: number;
    commonModifiersKeyword: number;
    modifiersKeyword: number;
};

interface NewProducerProps {
    wineId?: string;
    hideProducerFields?: boolean;
    wineData?: any;
    fromExternalInput?: boolean;
    dataLoaded?: boolean;
}

interface FormData {
    literal: string;
    newNarrowKeyword: string;
    filter_id: string;
    search_partialProducer: boolean;
    search_partial: boolean;
    regionCode: string;
    producerId: string;
    nextId: string;
    producerName: string;
    producerShow: string;
    producerLiteral: string;
    noiseMode: boolean;
    labelName: string;
    variety: number;
    color: string;
    sweetness: string;
    wineType: string;
    country: number;
    region: number;
    sub_region: number;
    appellations: number;
    all_appellations_tree: string;
    sub_appellations: number;
    noises: string[];
    producerNoises: string[];
    producerKeyword: string;
    baseKeyword: string;
    appellationKeyword: string;
    colorKeyword: string;
    commonModifiersKeyword: string;
    modifiersKeyword: string;
    customNotKeyword: string;
    globalNotKeyword: string;
    note: string;
    table: string;
    keyword: string;
}

interface OptionType {
    label: string;
    value: string;
    id?: string;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const NewProducer: React.FC<NewProducerProps> = ({ wineId, hideProducerFields, fromExternalInput, dataLoaded }) => {
    // Wine Table Pagination & Data State
    
    const [wineTableData, setWineTableData] = useState<any[]>([]);
    const [wineTablePage, setWineTablePage] = useState(1);
    const [hasMoreWineTableData, setHasMoreWineTableData] = useState(true);
    const [isLoadingWineTableData, setIsLoadingWineTableData] = useState(false);
    const [isLoadingMoreWineTableData, setIsLoadingMoreWineTableData] = useState(false);

    // Get getWines from API hook
    const { getWines } = useDirectInputApi();
    // Fetch wines for the table by producer literal
    const fetchWinesForTable = async (
        producerLiteral: string = producerFetchedFromWineId,
        page: number = 1,
        append: boolean = false
    ): Promise<void> => {
        // Don't fetch if no producer literal is provided
        if (!producerLiteral || !producerLiteral.trim()) {
            setWineTableData([]);
            return;
        }

        try {
            // Set appropriate loading state
            if (append) {
                setIsLoadingMoreWineTableData(true);
            } else {
                setIsLoadingWineTableData(true);
            }
            const per_page = 40;
            const params: any = {
                keywords: '', // Empty keywords when fetching by producer
                filter_id: '', // Empty filter_id when fetching by producer
                producers: producerLiteral, // Use producer literal directly
                search_partial: form.search_partial,
                page,
                per_page
            };
            const data = await getWines(params);
            const result = Array.isArray(data) ? data : (data?.data?.data?.result || []);
            // Update wines data based on append flag
            if (append) {
                setWineTableData(prev => [...prev, ...result]);
            } else {
                setWineTableData(result);
                setWineTablePage(1);
            }
            // Update pagination state
            setHasMoreWineTableData(result.length === per_page);
        } catch (error : any) {
            toast.error(error);
            setHasMoreWineTableData(false);
        } finally {
            // Clear loading states
            if (append) {
                setIsLoadingMoreWineTableData(false);
            } else {
                setIsLoadingWineTableData(false);
            }
        }
    };
    // Handler to populate form fields from selected appellation tree option
    const handleAppellationTreeSelect = (selected: any) => {
        if (!selected || !selected.appellation) return;
        const { appellation } = selected;
        setForm(prev => ({
            ...prev,
            country: appellation.country?.id || 0,
            region: appellation.region?.id || 0,
            sub_region: appellation['sub-region']?.id || 0,
            appellations: appellation.appellation?.id || 0,
            sub_appellations: appellation['sub-appellation']?.id || 0,
            all_appellations_tree: selected.name || ''
        }));
    };
    // Callback for loading more wine table data
    const handleLoadMoreWineTableData = () => {
        if (!isLoadingMoreWineTableData && hasMoreWineTableData && producerFetchedFromWineId) {
            const nextPage = wineTablePage + 1;
            setWineTablePage(nextPage);
            fetchWinesForTable(producerFetchedFromWineId, nextPage, true);
        }
    };


    const navigate = useNavigate();
    const location = useLocation();
    const { id: routeProducerId } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    // =============================================================================
    // API HOOKS
    // =============================================================================

    const { getCountryList } = useCountryApi();
    const { getRegionList } = useRegionApi();
    const { getSubRegionList } = useSubRegionApi();
    const { getAppellationList, getAppellationTree } = useAppellationApi();
    const { getSubAppellationList } = useSubAppellationApi();
    const {
        getVarietyList,
        getColorList,
        getSweetnessList,
        getTypeList, 
        createWine,
        updateWine,
        getWineById, 
        getWineByLiteral,
        createProducer, 
        getProducerById,
        getLiteral,
        getLiteralforProducer,
        getSpreadTables,
        getWordAliases,
        getWordEliminations,
        getWordChanges,
        getKeywordSubstitutions,
        getReservedWords,
        getVintages
    } = useDirectInputApi();
    const { externalTableData, externalCurrentIndex } = useExternalInputContext();

    // =============================================================================
    // MODE AND CONFIGURATION STATE
    // =============================================================================

    /**
     * Component Mode Determination
     * - When shouldHideProducerFields is true: Add Wine to Existing Producer Mode
     * - When editingWine is true: Edit Wine Mode
     * - Otherwise: New Producer Mode
     */
    const shouldHideProducerFields = hideProducerFields || 
        searchParams.get('hideProducerFields') === 'true' || 
        Boolean(routeProducerId);
    
    // Check if we're coming from external input and if dataLoaded is true
    const isFromExternalInput = fromExternalInput || searchParams.get('fromExternalInput') === 'true';
    const isDataLoaded = dataLoaded || searchParams.get('dataLoaded') === 'true';

    // Check if we're coming from validation and if dataLoaded is true
    const isFromValidation = searchParams.get('fromValidation') === 'true';
    const isValidationDataLoaded = searchParams.get('dataLoaded') === 'true';

    // Detect if coming from narrow mode
    const isFromNarrow = searchParams.get('fromNarrow') === 'true' || location.state?.fromNarrow;
    const narrowWineData = location.state?.narrowWineData;

    const producerIdFromRoute = routeProducerId;

    // Get initial form values, potentially from external input or validation
    const getInitialFormValues = () => {
        // Check if we're coming from external input and have wine data
        const externalWineData = (location.state as any)?.externalWineData;
        // Check if we're coming from validation and have wine data
        const validationWineData = (location.state as any)?.validationWineData;
        
        // Initialize with default values
        const initialForm: FormData = {
            literal: '',
            newNarrowKeyword: '',
            filter_id: '',
            search_partialProducer: true,
            search_partial: true,
            regionCode: '',
            producerId: producerIdFromRoute || '',
            nextId: '',
            producerName: '',
            producerShow: '',
            producerLiteral: '',
            noiseMode: false,
            labelName: '',
            variety: 0,
            color: '',
            sweetness: '',
            wineType: '',
            country: 0,
            region: 0,
            sub_region: 0,
            appellations: 0,
            all_appellations_tree: '',
            sub_appellations: 0,
            noises: [],
            producerNoises: [],
            producerKeyword: '',
            baseKeyword: '',
            appellationKeyword: '',
            colorKeyword: '',
            commonModifiersKeyword: '',
            modifiersKeyword: '',
            customNotKeyword: '',
            globalNotKeyword: '',
            note: '',
            table: '',
            keyword: ''
        };
        
        // If we have external wine data, use it to populate initial values
        if (isFromExternalInput && externalWineData) {
            // Store literal separately in the literal field
            if (externalWineData.literal) {
                initialForm.literal = externalWineData.literal;
            }
            
            // Only use label field for label name, never use literal
            if (externalWineData.label) {
                initialForm.labelName = externalWineData.label;
            }
            
            // Add any other fields from externalWineData that should be pre-populated
        }
        
        return initialForm;
    };

    // Form State
    const [form, setForm] = useState<FormData>(getInitialFormValues());

    // UI State
    const [submitting, setSubmitting] = useState(false);
    const [selectedWineRows, setSelectedWineRows] = useState<any[]>([]);
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [isLabelFieldFocused, setIsLabelFieldFocused] = useState(false);
    const labelBlurTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Producer-specific State
    const [modifiedProducer, setModifiedProducer] = useState<string | null>(null);
    const [regFromLiteral, setRegFromLiteral] = useState<string | null>(null);
    const [literalByRegCodeResult, setLiteralByRegCodeResult] = useState<string>('');
    const [literalOfProducer, setLiteralOfProducer] = useState<string>('');

    // Navigation warning protection
    const hasActiveWork = form.literal !== '' || form.producerName !== '' || form.labelName !== '' || submitting || selectedWineRows.length > 0;
    
    // Register/unregister this page for navigation protection
    useEffect(() => {
        let pageId = 'new-producer';
        let message = 'You have wine/producer creation work in progress. Are you sure you want to leave? Your progress will be lost.';
        
        if (shouldHideProducerFields && wineId) {
            pageId = 'edit-wine';
            message = 'You have wine editing work in progress. Are you sure you want to leave? Your changes will be lost.';
        } else if (shouldHideProducerFields) {
            pageId = 'add-wine-to-producer';
            message = 'You have wine addition work in progress. Are you sure you want to leave? Your progress will be lost.';
        }
        
        navigationWarningManager.registerPage(pageId, hasActiveWork);

        return () => {
            navigationWarningManager.unregisterPage(pageId);
        };
    }, [hasActiveWork, shouldHideProducerFields, wineId]);

    // Use the navigation warning hook
    const { navigateWithWarning } = useNavigationWarning({
        hasUnsavedWork: hasActiveWork
    });

    // Get shared context values, but we'll override them with our own local 
    const contextValues = useWineContext();
    
    // Use local state for transformation data to ensure consistency across all modes
    const [wordAliases, setWordAliases] = useState<Record<string, string>>({});
    const [wordEliminations, setWordEliminations] = useState<Set<string>>(new Set());
    const [wordEliminationsWines, setWordEliminationsWines] = useState<Set<string>>(new Set());
    const [wordChangesCustom, setWordChangesCustom] = useState<Record<string, string>>({});
    const [wordChangesMass, setWordChangesMass] = useState<Record<string, string>>({});
    const [spreadTables, setSpreadTables] = useState<any>(null);
    const [keywordSubstitutionsColor, setKeywordSubstitutionsColor] = useState<any>(null);
    const [keywordSubstitutionsColorNot, setKeywordSubstitutionsColorNot] = useState<any>(null);
    const [reservedWordsRedOpp, setReservedWordsRedOpp] = useState<any>(null);
    const [reservedWordsWhiteOpp, setReservedWordsWhiteOpp] = useState<any>(null);
    const [reservedWordsRoseOpp, setReservedWordsRoseOpp] = useState<any>(null);
    const [reservedWordsReserve, setReservedWordsReserve] = useState<any>(null);
    const [vintages, setVintages] = useState<any>(null);
    const [appellationTree , setAppellationTree] = useState<any>(null);
    const [isTransformationDataLoaded, setIsTransformationDataLoaded] = useState(false);
    const [spreadTablesLoaded, setSpreadTablesLoaded] = useState(false);
    
    // State for new generateKeyword logic
    const [colorNotSubstitutions, setColorNotSubstitutions] = useState<Record<string, string>>({});
    const [varietiesFullData, setVarietiesFullData] = useState<any[]>([]);
    
    const { isExternalMode } = contextValues;

    //Wine-specific State
    const [mergeModalWines, setMergeModalWines] = useState<any[]>([]);
    const [noiseEnabled, setNoiseEnabled] = useState(false);
    const [removedNoiseWords, setRemovedNoiseWords] = useState<string[]>([]);
    const [editingWine, setEditingWine] = useState(false);
    const [producerFetchedFromWineId, setProducerFetchedFromWineId] = useState<string>('');
    const [editingWineLiteral, setEditingWineLiteral] = useState<string>('');
    const [fetchedWineId, setFetchedWineId] = useState<any>(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [wineDataFetched, setWineDataFetched] = useState(false);
    const hasInitializedRef = React.useRef(false);
    // Debouncing for label name changes
    const labelNameChangeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Options State
    const [regCodes, setRegCodes] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [countryOptions, setCountryOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [regionOptions, setRegionOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [subRegionOptions, setSubRegionOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [varietiesOptions, setVarietiesOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [colorsOptions, setColorsOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [sweetnessOptions, setSweetnessOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [wineTypesOptions, setWineTypesOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [appellationsOptions, setAppellationsOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [noisesOptions, setNoisesOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [subAppellationsOptions, setSubAppellationsOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [appellationTreeOptions, setAppellationTreeOptions] = useState<OptionType[]>([{ label: '', value: '' }]);
    const [dropdownsLoaded, setDropdownsLoaded] = useState(false);
    const [originalKeyword, setOriginalKeyword] = useState<string>('');
    const [regeneratedKeyword, setRegeneratedKeyword] = useState<string>('');
    const [showKeywordDiff, setShowKeywordDiff] = useState(false);

    const handleRegenerateKeywords = () => {
    // Capture the current keyword as original before regenerating
    setOriginalKeyword(form.keyword || '');
    
    // Only use producerShow and labelName, ignore manual edits in keyword fields
    const baseForm: FormData = {
        ...form,
        producerKeyword: '',
        baseKeyword: '',
        appellationKeyword: '',
        colorKeyword: '',
        commonModifiersKeyword: '',
        modifiersKeyword: '',
        customNotKeyword: '',
        globalNotKeyword: '',
        keyword: '',
    };

    // If editing wine, keep producerKeyword as fetched from API
    if (editingWine) {
        baseForm.producerKeyword = form.producerKeyword;
    }

    // Generate keywords using the same logic as normal keyword generation
    let regenerated = generateKeywords(baseForm, keywordWeights);

    // Apply the same variety-specific transformation logic as in normal mode
    if (isTransformationDataLoaded && varietiesFullData.length > 0 && colorsOptions.length > 0) {
        regenerated = applyNewGenerateKeywordLogic(regenerated);
        // Re-run generateKeywords to properly manage !reserve after color substitutions
        regenerated = generateKeywords(regenerated, keywordWeights);
    }

    // Apply conflict removal logic as well
    if (isTransformationDataLoaded && (reservedWordsRedOpp || reservedWordsWhiteOpp || reservedWordsRoseOpp)) {
        regenerated = applyConflictRemovalLogic(regenerated);
        // Re-run generateKeywords to properly manage !reserve after conflict removal
        regenerated = generateKeywords(regenerated, keywordWeights);
    }

    // If editing wine, ensure producerKeyword is preserved in the form
    if (editingWine) {
        regenerated.producerKeyword = form.producerKeyword;
    }

    setForm(regenerated);
    setRegeneratedKeyword(regenerated.keyword);
    setShowKeywordDiff(true);
    };

    // Keyword weight state - initialized with current default values from the code
    const [keywordWeights, setKeywordWeights] = useState({
        producerKeyword: 1,
        baseKeyword: 1,
        appellationKeyword: 1,
        colorKeyword: 1,
        commonModifiersKeyword: 2,
        modifiersKeyword: 6
    });


    
    // =============================================================================
    // API FUNCTIONS
    // =============================================================================

    /**
     * Fetches keyword transformation data to be used in form processing
     * Used in: All Modes
     */
    const fetchKeywordTransformations = async (): Promise<void> => {
      try {
        const [
          eliminationsDataProducers, 
          eliminationsDataWines, 
          aliasesData, 
          wordChangesCustomData, 
          wordChangesMassData, 
          keywordSubstitutionsColorData, 
          keywordSubstitutionsColorNotData,
          reservedWordsRedOppData,
          reservedWordsWhiteOppData,
          reservedWordsRoseOppData,
          reservedWordsReserveData,
          vintagesData
        ] = await Promise.all([
          getWordEliminations({ type: 'PRODUCER', per_page: 999999 }),
          getWordEliminations({ type: 'WINE', per_page: 999999 }),
          getWordAliases({type:'WINE', per_page:999999}),
          getWordChanges({type: 'CUSTOM_CHANGES', per_page: 999999}),
          getWordChanges({type: 'MASS_CHANGES', per_page: 999999}),
          getKeywordSubstitutions({type: 'COLOR', per_page: 999999}),
          getKeywordSubstitutions({type: 'COLOR_NOT'}),
          getReservedWords({ type: 'RED_OPPOSITE'}),
          getReservedWords({ type: 'WHITE_OPPOSITE'}),
          getReservedWords({ type: 'ROSE_OPPOSITE'}),
          getReservedWords({type: 'RESERVE_WORD'}),
          getVintages()
        ]);

        // Store only the 'word' property in the Set
        const elimProducersArr = eliminationsDataProducers?.data?.data?.result || [];
        setWordEliminations(new Set(elimProducersArr.map((item: any) => item.word).filter(Boolean)));
        
        const elimWinesArr = eliminationsDataWines?.data?.data?.result || [];
        setWordEliminationsWines(new Set(elimWinesArr.map((item: any) => item.word).filter(Boolean)));

        // Transform aliases array to Record<string, string>
        const aliasesArr = aliasesData?.data?.data?.result || [];
        const aliasesRecord = aliasesArr.reduce((acc: Record<string, string>, curr: any) => {
          if (curr.word && curr.alias) acc[curr.alias] = curr.word;
          return acc;
        }, {});
        setWordAliases(aliasesRecord);

        // Transform keyword substitutions (colors)
        const colorsArr = keywordSubstitutionsColorData?.data?.data?.result || [];
        const colorsRecord = colorsArr.reduce((acc: Record<string, string>, curr: any) => {
          if (curr.keyword && curr.substitution) acc[curr.keyword] = curr.substitution;
          return acc;
        }, {});
        setKeywordSubstitutionsColor(colorsRecord);

        // Transform word_changes(custom and mass) to Record<string, string>
        const wordChangesCustomArr = wordChangesCustomData?.data?.data?.result || [];
        const wordChangesCustomRecord = wordChangesCustomArr.reduce((acc: Record<string, string>, curr: any) => {
          const [key, value] = Object.entries(curr)[0] || [];
          if (key && value) acc[String(key)] = String(value);
          return acc;
        }, {});
        setWordChangesCustom(wordChangesCustomRecord);

        const wordChangesMassArr = wordChangesMassData?.data?.data?.result || [];
        const wordChangesMassRecord = wordChangesMassArr.reduce((acc: Record<string, string>, curr: any) => {
          const [key, value] = Object.entries(curr)[0] || [];
          if (key && value) acc[String(key)] = String(value);
          return acc;
        }, {});
        setWordChangesMass(wordChangesMassRecord);

        setKeywordSubstitutionsColorNot(keywordSubstitutionsColorNotData?.data?.data?.result || []);
        setReservedWordsRedOpp(reservedWordsRedOppData?.data?.data?.result || []);
        setReservedWordsWhiteOpp(reservedWordsWhiteOppData?.data?.data?.result || []);
        setReservedWordsRoseOpp(reservedWordsRoseOppData?.data?.data?.result || []);
        setReservedWordsReserve(reservedWordsReserveData?.data?.data?.result || []);
        setVintages(vintagesData?.data?.data?.result || []);

        // Process data for new generateKeyword logic
        // Transform COLOR_NOT substitutions to Record<string, string>
        const colorNotArr = keywordSubstitutionsColorNotData?.data?.data?.result || [];
        const colorNotRecord = colorNotArr.reduce((acc: Record<string, string>, curr: any) => {
          if (curr.keyword && curr.substitution) acc[curr.keyword] = curr.substitution;
          return acc;
        }, {});
        setColorNotSubstitutions(colorNotRecord);
        
        setIsTransformationDataLoaded(true);
      } catch (error: any) {
        toast.error(error);
      }
    };

    /**
     * Core API Function Group
     * These functions handle the main data fetching operations for the component
     */

    /**
     * Fetches producer details by ID
     * Used in: Add Wine Mode, Edit Wine Mode
     * @param producerId - The ID of the producer to fetch
     * @returns Producer data or undefined if failed
     */
    const fetchProducer = async (producerId: string) => {
        try {
            const res = await getProducerById(producerId);
            return res;
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch producer details.');
            return undefined;
        }
    };

    const fetchWineById = async (ids: string[]): Promise<any[]> => {
        try {
            const wineDetails = await Promise.all(ids.map(id => getWineById(id)));
            return wineDetails.map(res => res?.data?.data || {});
        } catch (err) {
            return [];
        }
    };

    /**
     * Fetches wine ID (literal) based on region code
     */
    // Update the fetchWineId function to handle vintage consistently
    const fetchWineId = async (): Promise<void> => {
        if (editingWine) {
            return;
        }
        if (!form.regionCode) {
            setForm(prev => ({ ...prev, nextId: '' }));
            return;
        }
        try {
            const params: any = { reg_code: form.regionCode };
            
            // Include producer_literal for +wine mode
            if (shouldHideProducerFields && literalOfProducer) {
                params.producer_literal = literalOfProducer;
            }

            const res = await getLiteral(params);
            let wineId = res?.data?.data?.wineId || res?.data?.wineId || res?.data?.data || '';

            // Check for vintage from external input
            const externalData = externalTableData?.[externalCurrentIndex] || (location.state as any)?.externalWineData;
            if (externalData?.vintage && String(externalData.vintage).length === 4) {
                const formattedVintage = String(externalData.vintage)[0] + String(externalData.vintage).slice(2);
                wineId = `${wineId}${formattedVintage}`;
            }

            // Check for vintage from validation input
            const validationData = (location.state as any)?.validationWineData;
            if (validationData?.vintage && String(validationData.vintage).length === 4) {
                const formattedVintage = String(validationData.vintage)[0] + String(validationData.vintage).slice(2);
                wineId = `${wineId}${formattedVintage}`;
            }

            if (shouldHideProducerFields) {
                setLiteralByRegCodeResult(wineId);
            }
            
            setForm(prev => ({ ...prev, nextId: wineId }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch Wine ID.');
            setForm(prev => ({ ...prev, nextId: '' }));
        }
    };

    //fetch wine detail by id when we are navigated from wine tables edit icon
    /**
 * Form data for Edit Wine Mode is now handled in the improved useEffect
 * that ensures it only runs once after dropdowns are loaded
 */

    /**
     * Fetches and stores producer data when in hide producer fields mode
     */
    const fetchAndStoreProducer = async (): Promise<void> => {
        if (shouldHideProducerFields && producerIdFromRoute && !literalOfProducer) {
            const producerResult = await fetchProducer(producerIdFromRoute);
            if (producerResult?.data?.data) {
                const producerData = producerResult.data.data;
                const literal = producerData.literal || '';
                if (literalOfProducer === literal) return; // Already set, skip
                setLiteralOfProducer(literal);
                const name = producerData.name || '';
                setRegFromLiteral(literal.substring(0, 2));
                const producerFieldData = `${literal}-${name}`;
                setModifiedProducer(producerFieldData);
                
                // Extract producer keyword and producer noises and update form
                const producerKeyword = producerData.producer_keyword?.keyword || '';
                const producerNoises = Array.isArray(producerData.producer_noises) 
                    ? producerData.producer_noises.map((noise: { noise: string }) => noise.noise)
                    : [];
                    
                // Update form with producer keyword and noises
                setForm(prev => ({
                    ...prev,
                    producerKeyword: producerKeyword,
                    producerNoises: producerNoises
                }));
            }
        }
    };

    useEffect(() => {
        if (shouldHideProducerFields && literalOfProducer) {
            setProducerFetchedFromWineId(literalOfProducer);
        }
    }, [shouldHideProducerFields, literalOfProducer]);

    /**
     * Fetches literal by region code
     */
    const fetchLiteralByRegCode = async (): Promise<void> => {        
        if (regFromLiteral && literalOfProducer) {
            try {
                const params = {
                    reg_code: regFromLiteral,
                    producer_literal: literalOfProducer
                };
                const res = await getLiteral(params);
                setLiteralByRegCodeResult(res?.data?.data ? String(res.data.data) : '');
            } catch (err) {
                setLiteralByRegCodeResult('');
            }
        } else {
            setLiteralByRegCodeResult('');
        }
    };

    // =============================================================================
    // DROPDOWN OPTIONS FETCH FUNCTIONS
    // =============================================================================

    /**
     * Form Options Group
     * These functions handle fetching data for form dropdowns and selections
     * They are used across all modes to populate form fields
     */

    /**
     * Fetches countries list for dropdown
     * Used in: All Modes
     */
    const fetchCountries = async (): Promise<void> => {
        try {
            const res = await getCountryList({per_page:999999});
            const result = res?.data?.data?.result || res?.data?.result || [];
            if (Array.isArray(result)) {
                setCountryOptions(result.map((c: any) => ({ 
                    label: c.name, 
                    value: c.id, 
                    id: c.id 
                })));
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch countries list.');
            setCountryOptions([{ label: '', value: '' }]);
        }
    };

    // Fetch all regions without country filter for dropdown
    const fetchRegions = async (): Promise<void> => {
    try {
        const res = await getRegionList({ per_page: 999999 }); // Remove country_id param
        const result = res?.data?.data?.result || res?.data?.result || [];
        if (Array.isArray(result)) {
        setRegionOptions(result.map((r: any) => ({
            label: r.name,
            value: r.id,
            id: r.id
        })));
        }
    } catch (err: any) {
        toast.error(err?.message || 'Failed to fetch regions.');
        setRegionOptions([{ label: '', value: '' }]);
    }
    };

    // Fetch all subregions without region filter for dropdown
    const fetchSubRegions = async (): Promise<void> => {
    try {
        const res = await getSubRegionList({ per_page: 999999 }); // Remove region_id param
        const result = res?.data?.data?.result || res?.data?.result || [];
        if (Array.isArray(result)) {
        setSubRegionOptions(result.map((s: any) => ({
            label: s.name,
            value: s.id,
            id: s.id
        })));
        }
    } catch (err: any) {
        toast.error(err?.message || 'Failed to fetch subregions.');
        setSubRegionOptions([{ label: '', value: '' }]);
    }
    };

    /**
     * Fetches region codes for dropdown
     */
    const fetchRegCodes = async (): Promise<void> => {
        try {
            const res = await getRegionList({per_page: 999999});
            const result = res?.data?.data?.result || [];
            if (Array.isArray(result)) {
                const mapped = result
                    .filter((region: any) => region.region_code && typeof region.region_code === 'string' && region.region_code.trim() !== '')
                    .map((region: any) => ({
                        label: region.region_code,
                        value: region.region_code
                    }));
                // Remove all duplicates and sort in ascending order
                const uniqueMapped = Array.from(new Set(mapped.map(item => item.value))).map(value => {
                    return mapped.find(item => item.value === value);
                });
                uniqueMapped.sort((a, b) => {
                    if (a && b && a.value && b.value) {
                        return a.value.localeCompare(b.value);
                    }
                    return 0;
                });
                setRegCodes(uniqueMapped.filter((item): item is OptionType => !!item));
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch reg codes list.');
            setRegCodes([{ label: '', value: '' }]);
        }
    };

    /**
     * Fetches colors list for dropdown
     */
    const fetchColors = async (): Promise<void> => {
        try {
            const res = await getColorList({per_page:999999});
            const result = res?.data?.data || [];
            if (Array.isArray(result)) {
                setColorsOptions(result.map((color: any) => ({ 
                    label: color, 
                    value: color 
                })));
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch colors list.');
            setColorsOptions([{ label: '', value: '' }]);
        }
    };

    /**
     * Fetches sweetness levels for dropdown
     */
    const fetchSweetness = async (): Promise<void> => {
        try {
            const res = await getSweetnessList({per_page:999999});
            const result = res?.data?.data || [];
            if (Array.isArray(result)) {
                setSweetnessOptions(result.map((sweetness: any) => ({ 
                    label: sweetness, 
                    value: sweetness
                })));
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch sweetness list.');
            setSweetnessOptions([{ label: '', value: '' }]);
        }
    };

    /**
     * Fetches wine types for dropdown
     */
    const fetchTypes = async (): Promise<void> => {
        try {
            const res = await getTypeList({per_page:999999});
            const result = res?.data?.data || [];
            if (Array.isArray(result)) {
                setWineTypesOptions(result.map((type: any) => ({ 
                    label: type, 
                    value: type 
                })));
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch wine types list.');
            setWineTypesOptions([{ label: '', value: '' }]);
        }
    };

    /**
     * Fetches varieties for dropdown
     */
    const fetchVarieties = async (): Promise<void> => {
        try {
            const res = await getVarietyList({per_page:999999});
            const result = res?.data?.data?.result || [];
            if (Array.isArray(result)) {
                setVarietiesOptions(result.map((v: any) => ({ 
                    label: v.name, 
                    value: v.id 
                })));
                // Store full varieties data for the new generateKeyword logic
                setVarietiesFullData(result);
            }
        } catch (err: any) {
            toast.error(err);
            setVarietiesOptions([{ label: '', value: '' }]);
        }
    };

    /**
     * Fetches appellations for dropdown using locales API
     */
    const fetchAppellations = async (): Promise<void> => {
        try {
            const res = await getAppellationList({per_page:999999});
            const result = res?.data?.data?.result || [];
            if (Array.isArray(result)) {
                setAppellationsOptions(result.map((appellation: any) => ({ 
                    label: appellation.name, 
                    value: appellation.id 
                })));
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch appellations.');
            setAppellationsOptions([{ label: '', value: '' }]);
        }
    }

    /**
     * Fetches sub-appellations for dropdown
     */
    const fetchSubAppellations = async (): Promise<void> => {
        try {
            const res = await getSubAppellationList({per_page: 999999});
            const result = res?.data?.data?.result || [];
            if (Array.isArray(result)) {
                setSubAppellationsOptions(result.map((subappellation: any) => ({
                    label: subappellation.name,
                    value: subappellation.id
                })));
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to fetch sub-appellations.');
            setSubAppellationsOptions([{ label: '', value: '' }]);
        }
    };

    function getAppellationTreeLabel(item: any): string {
        if (!item || !item.type || !item.appellation) return '';

        const { type, name, appellation } = item;

        switch (type) {
            case 'Country':
                return appellation.country?.name || name || '';
            case 'Region':
                return [
                    appellation.country?.name,
                    appellation.region?.name || name
                ].filter(Boolean).join(' / ');
            case 'Sub-Region':
                return [
                    appellation.country?.name,
                    appellation.region?.name,
                    appellation['sub-region']?.name || name
                ].filter(Boolean).join(' / ');
            case 'Appellation':
                return [
                    appellation.country?.name,
                    appellation.region?.name,
                    appellation['sub-region']?.name,
                    appellation.appellation?.name || name
                ].filter(Boolean).join(' / ');
            case 'Sub-Appellation':
                return [
                    appellation.country?.name,
                    appellation.region?.name,
                    appellation['sub-region']?.name,
                    appellation.appellation?.name,
                    appellation['sub-appellation']?.name || name
                ].filter(Boolean).join(' / ');
            default:
                return name || '';
        }
    };

    /**
     * Fetches appellation tree for dropdown
     */
    const fetchAppellationTree = async (): Promise<void> => {
        try {
            const res = await getAppellationTree();
            const result = res?.data?.data || [];
            if (Array.isArray(result)) {
                // Store the full tree data for later use
                setAppellationTree(result);
                
                setAppellationTreeOptions(
                    result.map((item: any) => ({
                        label: getAppellationTreeLabel(item),
                        value: item.name
                    }))
                );
            }
        } catch (err: any) {
            toast.error(err);
            setAppellationTreeOptions([{ label: '', value: '' }]);
        }
    };

        /**
     * Fetches noises for dropdown
     */
    const fetchNoise = () => {
        // If noiseEnabled is true, set noisesOptions from labelName word list
        if (noiseEnabled && form.labelName) {
            const words = form.labelName.split(/\s+/).filter(Boolean);
            setNoisesOptions(words.map(word => ({ label: word, value: word })));
            return;
        }
    };

    //For keyword generation
    //fetch spread tables
     
    // Track previous region code to prevent redundant API calls
    const previousRegionCodeRef = React.useRef<string>('');
    
    const fetchSpreadTables = async (): Promise<void> => {
        // For +wine and edit modes, we need to use regFromLiteral if it's set
        // For normal mode, use form.regionCode
        let regionCodeToUse = '';
        
        if (shouldHideProducerFields && regFromLiteral) {
            // For +wine mode: find matching region code from regFromLiteral
            const matchedRegCode = regCodes.find(code => code.value.startsWith(regFromLiteral));
            regionCodeToUse = matchedRegCode ? matchedRegCode.value : regFromLiteral;
        } else {
            // For normal mode: use form.regionCode
            regionCodeToUse = form.regionCode;
        }
        
        // Check if this is a duplicate call with the same region code
        if (regionCodeToUse && regionCodeToUse === previousRegionCodeRef.current) {
            return;
        }
        
        
        if (regionCodeToUse) {
            // Save the current region code to prevent duplicate calls
            previousRegionCodeRef.current = regionCodeToUse;
            
            try {
                // Use correct param name: region_codes
                const res = await getSpreadTables({ region_codes: regionCodeToUse });
                const newSpreadTables = res?.data?.data || {};
                setSpreadTables(newSpreadTables);
            } catch (err) {
                setSpreadTables({});
            }
        } else {
            setSpreadTables({});
        }
    };

    /**
     * Open Modal for merging wines
    */
    const handleMergeSelected = async (): Promise<void> => {
    if (selectedWineRows.length < 1 || selectedWineRows.length > 3) return;
        const ids = selectedWineRows.map(row => row.id);
        const winesData = await fetchWineById(ids);
        setMergeModalWines(winesData);
        setShowMergeModal(true);
    };


    /**
     * Initializes all dropdown options
     */
    const initializeDropdownOptions = async (): Promise<void> => {
        await Promise.all([
            fetchCountries(),
            fetchRegCodes(),
            fetchColors(),
            fetchSweetness(),
            fetchTypes(),
            fetchVarieties(),
            fetchAppellations(),
            fetchSubAppellations(),
            fetchRegions(),         
            fetchSubRegions(),      
            fetchAppellationTree()
        ]);
        await fetchNoise();
        setDropdownsLoaded(true);
    };

    /**
     * Applies conflict removal logic based on the current color and producer/label content
     * This function checks for conflicting reserved words and removes color-specific substitutions
     */
    const applyConflictRemovalLogic = (formData: FormData): FormData => {
        let updatedForm = { ...formData };
        
        // Determine which color to use for conflict removal logic
        let colorForLogic = '';
        
        // Priority 1: If color is directly selected from dropdown, use that
        if (formData.color && formData.color.trim() !== '') {
            colorForLogic = formData.color.toLowerCase();
        }
        // Priority 2: If variety is selected and no direct color, use variety's default color
        else if (formData.variety && formData.variety !== 0) {
            const varietyId = Number(formData.variety);
            const selectedVariety = varietiesFullData.find((variety: any) => Number(variety.id) === varietyId);
            if (selectedVariety && selectedVariety.default_color) {
                colorForLogic = selectedVariety.default_color.toLowerCase();
            }
        }
        
        // If no color is determined, return without changes
        if (!colorForLogic) {
            return updatedForm;
        }
        
        // Apply conflict removal rule using the determined color
        const getReservedWordsForColor = (color: string): string[] => {
            switch (color) {
                case 'red':
                    return (reservedWordsRedOpp || []).map((item: any) => item.word || item).filter(Boolean);
                case 'white':
                    return (reservedWordsWhiteOpp || []).map((item: any) => item.word || item).filter(Boolean);
                case 'rose':
                case 'rosé':
                    return (reservedWordsRoseOpp || []).map((item: any) => item.word || item).filter(Boolean);
                default:
                    return [];
            }
        };

        const reservedWords = getReservedWordsForColor(colorForLogic);
        if (reservedWords.length > 0) {
            // Check if any reserved words are found in producer show or label name
            const producerShow = (formData.producerShow || '').toLowerCase();
            const labelName = (formData.labelName || '').toLowerCase();
            
            const hasConflictingWord = reservedWords.some(word => {
                const lowerWord = word.toLowerCase();
                return producerShow.includes(lowerWord) || labelName.includes(lowerWord);
            });

            // If conflicting word is found, remove ALL variety-specific substitutions
            if (hasConflictingWord) {
                console.log('⚠️ Conflicting word found, removing color-specific substitutions');
                let cleanedGlobalNots = updatedForm.globalNotKeyword || '';
                
                // Remove !reserve temporarily to avoid losing it
                cleanedGlobalNots = cleanedGlobalNots.replace(/&?!reserve/g, '');
                
                // Split into terms for better manipulation
                let globalNotsTerms = cleanedGlobalNots ? cleanedGlobalNots.split('&').filter(Boolean) : [];
                
                // Remove ALL variety-specific substitution terms from all colors
                Object.values(colorNotSubstitutions).forEach(substitution => {
                    if (substitution) {
                        const subTerms = substitution.split('&').filter(Boolean);
                        globalNotsTerms = globalNotsTerms.filter(term => !subTerms.includes(term));
                    }
                });
                
                // Rebuild the cleaned global nots string
                cleanedGlobalNots = globalNotsTerms.join('&');
                
                // Do NOT add !reserve here - let the main generateKeywords function handle it
                updatedForm.globalNotKeyword = cleanedGlobalNots;
            }
        }

        return updatedForm;
    };

    /**
     * New generateKeyword logic implementation
     * Modified to use color dropdown data instead of only variety's default color
     */
    const applyNewGenerateKeywordLogic = (formData: FormData): FormData => {
        let updatedForm = { ...formData };
        
        // Determine which color to use for keyword generation logic
        let colorForLogic = '';
        
        // Priority 1: If color is directly selected from dropdown, use that
        if (formData.color && formData.color.trim() !== '') {
            colorForLogic = formData.color.toLowerCase();
        }
        // Priority 2: If variety is selected and no direct color, use variety's default color
        else if (formData.variety && formData.variety !== 0) {
            const varietyId = Number(formData.variety);
            const selectedVariety = varietiesFullData.find((variety: any) => Number(variety.id) === varietyId);
            if (selectedVariety && selectedVariety.default_color) {
                const defaultColor = selectedVariety.default_color.toLowerCase();
                
                // Find the actual color option value that matches the variety's default color
                const findColorOption = (targetColor: string) => {
                    return colorsOptions.find(option => 
                        option.value.toLowerCase() === targetColor.toLowerCase() ||
                        option.label.toLowerCase() === targetColor.toLowerCase()
                    );
                };
                
                // Use the variety's default color directly
                const colorOption = findColorOption(defaultColor);
                const colorValue = colorOption ? colorOption.value : defaultColor;
                
                // Set the variety's default color in the form
                updatedForm.color = colorValue;
                colorForLogic = colorValue.toLowerCase();
            }
        }
        
        // If no color is determined, return without changes
        if (!colorForLogic) {
            return updatedForm;
        }
        
        // Check if the determined color exists in COLOR_NOT substitutions
        const substitutionValue = colorNotSubstitutions[colorForLogic];
        if (!substitutionValue) {
            return updatedForm;
        }

        // Check if substitution is already applied to avoid duplicates
        const currentGlobalNots = updatedForm.globalNotKeyword || '';
        const substitutionTerms = substitutionValue.split('&').filter(Boolean);
        const currentTerms = currentGlobalNots.split('&').filter(Boolean);
        
        // If all substitution terms are already present, skip processing
        const alreadyApplied = substitutionTerms.every(term => currentTerms.includes(term));
        if (alreadyApplied) {
            return updatedForm;
        }
                
        // Add substitution value to global_nots (do NOT manage !reserve here)
        let globalNots = updatedForm.globalNotKeyword || '';
        
        // Remove existing !reserve temporarily to avoid duplication when re-adding later
        globalNots = globalNots.replace(/&?!reserve/g, '');        
        
        // Split the current global nots into individual terms for better deduplication
        let globalNotsTerms = globalNots ? globalNots.split('&').filter(Boolean) : [];
        
        // Remove ALL existing variety-specific substitution terms to prevent duplicates
        Object.values(colorNotSubstitutions).forEach(substitution => {
            if (substitution) {
                const subTerms = substitution.split('&').filter(Boolean);
                globalNotsTerms = globalNotsTerms.filter(term => !subTerms.includes(term));
            }
        });
        
        // Add the new substitution terms
        globalNotsTerms = [...globalNotsTerms, ...substitutionTerms];
        
        // Remove any duplicates using Array.filter
        globalNotsTerms = globalNotsTerms.filter((term, index) => globalNotsTerms.indexOf(term) === index);
        
        // Rebuild the global nots string
        globalNots = globalNotsTerms.join('&');
        
        // Do NOT add !reserve here - let the main generateKeywords function handle it

        updatedForm.globalNotKeyword = globalNots;

        // Apply conflict removal rule using the determined color
        const getReservedWordsForColor = (color: string): string[] => {
            switch (color) {
                case 'red':
                    return (reservedWordsRedOpp || []).map((item: any) => item.word || item).filter(Boolean);
                case 'white':
                    return (reservedWordsWhiteOpp || []).map((item: any) => item.word || item).filter(Boolean);
                case 'rose':
                case 'rosé':
                    return (reservedWordsRoseOpp || []).map((item: any) => item.word || item).filter(Boolean);
                default:
                    return [];
            }
        };

        const reservedWords = getReservedWordsForColor(colorForLogic);
        if (reservedWords.length > 0) {
            // Check if any reserved words are found in producer show or label name
            const producerShow = (formData.producerShow || '').toLowerCase();
            const labelName = (formData.labelName || '').toLowerCase();
            
            const hasConflictingWord = reservedWords.some(word => {
                const lowerWord = word.toLowerCase();
                return producerShow.includes(lowerWord) || labelName.includes(lowerWord);
            });

            // If conflicting word is found, remove ALL variety-specific substitutions
            if (hasConflictingWord) {
                let cleanedGlobalNots = updatedForm.globalNotKeyword || '';
                
                // Remove !reserve temporarily to avoid losing it
                cleanedGlobalNots = cleanedGlobalNots.replace(/&?!reserve/g, '');
                
                // Split into terms for better manipulation
                let globalNotsTerms = cleanedGlobalNots ? cleanedGlobalNots.split('&').filter(Boolean) : [];
                
                // Remove ALL variety-specific substitution terms from all colors
                Object.values(colorNotSubstitutions).forEach(substitution => {
                    if (substitution) {
                        const subTerms = substitution.split('&').filter(Boolean);
                        globalNotsTerms = globalNotsTerms.filter(term => !subTerms.includes(term));
                    }
                });
                
                // Rebuild the cleaned global nots string
                cleanedGlobalNots = globalNotsTerms.join('&');
                
                // Do NOT add !reserve here - let the main generateKeywords function handle it
                updatedForm.globalNotKeyword = cleanedGlobalNots;
            }
        }

        return updatedForm;
    };

    /**
     * Generates keywords based on input data
     * This function encapsulates the logic previously in handleChange
     * Can be called directly when form fields are pre-populated
     */
    const generateKeywords = (formData: FormData, weights: KeywordWeights) => {
        // Guard against undefined/null formData
        if (!formData) {
            return {
                literal: '',
                newNarrowKeyword: '',
                filter_id: '',
                search_partialProducer: true,
                search_partial: true,
                regionCode: '',
                producerId: '',
                nextId: '',
                producerName: '',
                producerShow: '',
                producerLiteral: '',
                noiseMode: false,
                labelName: '',
                variety: 0,
                color: '',
                sweetness: '',
                wineType: '',
                country: 0,
                region: 0,
                sub_region: 0,
                appellations: 0,
                all_appellations_tree: '',
                sub_appellations: 0,
                noises: [],
                producerNoises: [],
                producerKeyword: '',
                baseKeyword: '',
                appellationKeyword: '',
                colorKeyword: '',
                commonModifiersKeyword: '',
                modifiersKeyword: '',
                customNotKeyword: '',
                globalNotKeyword: '',
                note: '',
                table: '',
                keyword: ''
            };
        }

        // Generate producer keyword from producerShow
        let updatedForm = { ...formData };
        
        // Check if we're in "add wine to producer" mode (shouldHideProducerFields is true)
        // In this mode, preserve the existing producerKeyword from API data
        if (shouldHideProducerFields) {
            // Keep the existing producerKeyword - don't regenerate it
            // This preserves the producer keyword fetched from the API
        } else {
            // Only regenerate producer keyword in new producer mode
            if (!formData.producerShow || formData.producerShow.trim() === '') {
                updatedForm.producerKeyword = '';
            } else if (formData.producerShow) {
                const processedInput = applyAliases(formData.producerShow, wordAliases);
                const tokens = tokenize(processedInput);
                const eliminationList = Array.from(wordEliminations).filter((a): a is string => typeof a === 'string');
                const finalTokens = removeEliminations(tokens, eliminationList);
                // Remove single word eliminations
                const wordElimsSet = new Set(wordEliminations);
                const filteredWords = finalTokens.filter(word => !wordElimsSet.has(word));
                updatedForm.producerKeyword = filteredWords.join('&');
            }
        }
        
        // Generate wine keywords from labelName
        // Clear all wine-related keywords if label name is empty
        if (!formData.labelName || formData.labelName.trim() === '') {
            updatedForm = { 
                ...updatedForm, 
                modifiersKeyword: '', 
                baseKeyword: '', 
                appellationKeyword: '', 
                colorKeyword: '', 
                commonModifiersKeyword: '', 
            };
        } else if (formData.labelName) {
            const input = String(formData.labelName || '').toLowerCase().trim();

            if (!input) {
            updatedForm = { 
                ...updatedForm, 
                modifiersKeyword: '', 
                baseKeyword: '', 
                appellationKeyword: '', 
                colorKeyword: '', 
                commonModifiersKeyword: '', 
            };
            } else {
            // Process input
            const processedInput = applyAliases(input, wordAliases);
            const tokens = tokenize(processedInput);
            const wineEliminations = Array.from(wordEliminationsWines || new Set<string>());
            let afterElimination = removeEliminations(tokens, wineEliminations as string[]);

            // Remove single wine eliminations
            const wineWordElimsSet = new Set(
                Array.from(wordEliminationsWines || new Set()).map((s: any) => String(s).toLowerCase())
            );
            let words = afterElimination.filter(w => !wineWordElimsSet.has(w));

            // Helper to join tokens with &
            const joinTokens = (arr: string[]) => arr.join('&');
            let joined = joinTokens(words);

            // Apply wordChangesCustom (multi-word, always prefix with &)
            if (wordChangesCustom && typeof wordChangesCustom === 'object') {
                const customKeys = Object.keys(wordChangesCustom).sort((a, b) => b.length - a.length);
                for (const key of customKeys) {
                // Build regex to match key as sequence, ignoring non-alphanum between words
                const keyPattern = key.split('&').map(w => escapeRegex(w)).join('[^a-z0-9]+');
                const regex = new RegExp(keyPattern, 'gi');
                joined = joined.replace(regex, (match) => {
                    let mapped = (wordChangesCustom as Record<string, string>)[key];
                    if (typeof mapped === 'string') {
                    // Always prefix with & (if not already)
                    return mapped.startsWith('&') ? mapped : ('&' + mapped);
                    }
                    return match;
                });
                }
            }

            // Apply wordChangesMass (single word, use as-is)
            if (wordChangesMass && typeof wordChangesMass === 'object') {
                const massKeys = Object.keys(wordChangesMass).sort((a, b) => b.length - a.length);
                for (const key of massKeys) {
                // Build regex to match key as a word (with & or start/end)
                const regex = new RegExp(`(?<=^|&)${escapeRegex(key)}(?=&|$)`, 'gi');
                joined = joined.replace(regex, (match) => {
                    let mapped = (wordChangesMass as Record<string, string>)[key];
                    if (typeof mapped === 'string') {
                    return mapped;
                    }
                    return match;
                });
                }
            }

            // Split back into words (remove empty)
            words = joined.split('&').map(w => w.trim()).filter(Boolean);

            // Categorize words using spread tables
            // FIX: Use the correct region code for spread table lookup
            let regionCodeForSpreadTables = '';
    
            if (shouldHideProducerFields && regFromLiteral) {
                // For +wine mode: find matching region code from regFromLiteral
                const matchedRegCode = regCodes.find(code => code.value.startsWith(regFromLiteral));
                regionCodeForSpreadTables = matchedRegCode ? matchedRegCode.value : regFromLiteral;
            } else {
                // For normal mode: use form.regionCode
                regionCodeForSpreadTables = formData.regionCode;
            }
                        
            const regionSpread = regionCodeForSpreadTables && spreadTables?.[regionCodeForSpreadTables] || null;

            const findInSpread = (type: string, word: string, allWords: string[]) => {
                if (!regionSpread?.[type]) return false;
                return regionSpread[type].some((item: any) => {
                    const keyword = (item.keyword || '').toLowerCase();
                    const keywordParts = keyword.split('&').map((part: string) => part.trim()).filter(Boolean);
                    
                    // For single-word keywords, only match exact word
                    if (keywordParts.length === 1) {
                        return keywordParts[0] === word;
                    }
                    
                    // For multi-word keywords, check if ALL parts of the keyword are present in allWords
                    // and the current word is part of this complete keyword
                    if (keywordParts.includes(word)) {
                        // Check if all parts of this compound keyword are present in the current text
                        return keywordParts.every((part: string) => allWords.includes(part));
                    }
                    
                    return false;
                });
            };

            // Helper function to find compound keywords that match
            const findCompoundKeywords = (allWords: string[]) => {
                const compoundMatches = new Set<string>();
                const categories = ['APPELLATION', 'COLOR', 'BASE', 'COMMON_MODIFIER'];
                
                for (const category of categories) {
                    if (regionSpread?.[category]) {
                        regionSpread[category].forEach((item: any) => {
                            const keyword = (item.keyword || '').toLowerCase();
                            const keywordParts = keyword.split('&').map((part: string) => part.trim()).filter(Boolean);
                            
                            // Only process multi-word keywords
                            if (keywordParts.length > 1) {
                                // Check if all parts of this compound keyword are present in allWords
                                if (keywordParts.every((part: string) => allWords.includes(part))) {
                                    // Mark all parts of this compound keyword as used
                                    keywordParts.forEach((part: any) => compoundMatches.add(`${part}:${category}`));
                                }
                            }
                        });
                    }
                }
                return compoundMatches;
            };

            // Helper function to find the best category for a word with priority logic
            const findBestCategory = (word: string, allWords: string[], compoundMatches: Set<string>) => {
                // First check if this word is part of a compound keyword
                const categories = ['APPELLATION', 'COLOR', 'BASE', 'COMMON_MODIFIER'];
                for (const category of categories) {
                    if (compoundMatches.has(`${word}:${category}`)) {
                        return category;
                    }
                }
                
                // Then look for exact single-word matches (highest priority)
                for (const category of categories) {
                    if (regionSpread?.[category]) {
                        const hasExactMatch = regionSpread[category].some((item: any) => {
                            const keyword = (item.keyword || '').toLowerCase();
                            const keywordParts = keyword.split('&').map((part: string) => part.trim()).filter(Boolean);
                            return keywordParts.length === 1 && keywordParts[0] === word;
                        });
                        if (hasExactMatch) return category;
                    }
                }
                
                return null;
            };

            // Find all compound keyword matches first
            const compoundMatches = findCompoundKeywords(words.map(w => w.toLowerCase()));

            let baseWords: string[] = [];
            let appellationWords: string[] = [];
            let colorWords: string[] = [];
            let commonModifierWords: string[] = [];
            let modifierWords: string[] = [];

            for (const word of words) {
                const lowerWord = word.toLowerCase();
                const bestCategory = findBestCategory(lowerWord, words.map(w => w.toLowerCase()), compoundMatches);
                
                if (bestCategory === 'APPELLATION') {
                    appellationWords.push(lowerWord);
                } else if (bestCategory === 'COLOR') {
                    colorWords.push(lowerWord);
                } else if (bestCategory === 'BASE') {
                    baseWords.push(lowerWord);
                } else if (bestCategory === 'COMMON_MODIFIER') {
                    commonModifierWords.push(lowerWord);
                } else {
                    modifierWords.push(lowerWord);
                }
            }

            // NEW REQUIREMENT: Add "reserve" to the start of common modifiers if present
            const containsReserve = words.some(word => word.toLowerCase() === 'reserve');
            if (containsReserve) {
                // Remove "reserve" from other arrays if it exists
                baseWords = baseWords.filter(word => word.toLowerCase() !== 'reserve');
                appellationWords = appellationWords.filter(word => word.toLowerCase() !== 'reserve');
                colorWords = colorWords.filter(word => word.toLowerCase() !== 'reserve');
                modifierWords = modifierWords.filter(word => word.toLowerCase() !== 'reserve');
                
                // Remove "reserve" from common modifiers first if it exists anywhere
                commonModifierWords = commonModifierWords.filter(word => word.toLowerCase() !== 'reserve');
                
                // Add "reserve" to the start of common modifiers
                commonModifierWords.unshift('reserve');
                
            }

            // Build keywords with & prefix
            const prefixJoin = (words: string[]) => words.length > 0 ? '&' + words.join('&') : '';

            updatedForm.baseKeyword = prefixJoin(baseWords);
            updatedForm.appellationKeyword = prefixJoin(appellationWords);
            updatedForm.colorKeyword = prefixJoin(colorWords);
            updatedForm.commonModifiersKeyword = prefixJoin(commonModifierWords);
            updatedForm.modifiersKeyword = prefixJoin(modifierWords);

            // Apply color substitutions
            if (updatedForm.colorKeyword && keywordSubstitutionsColor) {
                const substitutedColors = colorWords.map(word => {
                const foundKey = Object.keys(keywordSubstitutionsColor).find(
                    k => k.replace(/^&/, '').toLowerCase() === word
                );
                let mapped = foundKey ? keywordSubstitutionsColor[foundKey] : word;
                return typeof mapped === 'string' ? mapped.replace(/^&+/, '') : mapped;
                });
                updatedForm.colorKeyword = substitutedColors.length > 0 ? '&' + substitutedColors.join('&') : '';
            }
            }
        }
        
        // Generate final keyword - UPDATED to use the passed weights parameter
        const repeat = (val: string, times: number) => {
            if (!val) return '';
            return Array(times).fill(val).join('&');
        };

        // If both producer show and label name are empty, clear all keyword fields
        // BUT in shouldHideProducerFields mode, don't clear if we have a producerKeyword from API
        if ((!formData.producerShow || formData.producerShow.trim() === '') &&
            (!formData.labelName || formData.labelName.trim() === '')) {
            if (shouldHideProducerFields && updatedForm.producerKeyword) {
                // Keep the producer keyword and generate final keyword with just that
                const keywordParts = [
                    repeat(updatedForm.producerKeyword, weights.producerKeyword)
                ].filter(Boolean);
                // Always add customNotKeyword with weight 1, just before globalNotKeyword
                if (updatedForm.customNotKeyword && updatedForm.customNotKeyword.trim()) {
                    keywordParts.push(updatedForm.customNotKeyword.trim());
                }
                // Add global nots at the end if they exist
                if (updatedForm.globalNotKeyword && updatedForm.globalNotKeyword.trim()) {
                    keywordParts.push(updatedForm.globalNotKeyword.trim());
                }
                
                updatedForm.keyword = keywordParts
                    .join('&')
                    .replace(/&{2,}/g, '&')
                    .replace(/^&+|&+$/g, '');
                // Don't override globalNotKeyword in hide producer fields mode if it's already set
                if (!updatedForm.globalNotKeyword || updatedForm.globalNotKeyword.trim() === '') {
                    updatedForm.globalNotKeyword = '&!reserve';
                }
            } else {
                updatedForm.keyword = '';
                updatedForm.globalNotKeyword = '';
            }
            return updatedForm;
        }

        const producerKeyword = updatedForm.producerKeyword || '';

        // Use the weights parameter instead of the state directly
        const keywordParts = [
            repeat(producerKeyword, weights.producerKeyword),
            repeat(updatedForm.baseKeyword, weights.baseKeyword),
            repeat(updatedForm.appellationKeyword, weights.appellationKeyword),
            repeat(updatedForm.colorKeyword, weights.colorKeyword),
            repeat(updatedForm.commonModifiersKeyword, weights.commonModifiersKeyword),
            repeat(updatedForm.modifiersKeyword, weights.modifiersKeyword)
        ].filter(Boolean);
        // Always add customNotKeyword with weight 1, just before globalNotKeyword
        if (updatedForm.customNotKeyword && updatedForm.customNotKeyword.trim()) {
            keywordParts.push(updatedForm.customNotKeyword.trim());
        }
        // Add global nots at the end if they exist
        if (updatedForm.globalNotKeyword && updatedForm.globalNotKeyword.trim()) {
            keywordParts.push(updatedForm.globalNotKeyword.trim());
        }

        updatedForm.keyword = keywordParts
            .join('&')
            .replace(/&{2,}/g, '&')
            .replace(/^&+|&+$/g, '');

        // Check if "reserve" appears in any keyword field to manage globalNotKeyword
        const containsExactReserve = (str: string) =>
            str?.split('&').map(w => w.trim()).includes('reserve');

        const hasReserveWord = (
            containsExactReserve(updatedForm.producerKeyword) ||
            containsExactReserve(updatedForm.baseKeyword) ||
            containsExactReserve(updatedForm.appellationKeyword) ||
            containsExactReserve(updatedForm.colorKeyword) ||
            containsExactReserve(updatedForm.commonModifiersKeyword) ||
            containsExactReserve(updatedForm.modifiersKeyword)
        );

        // ALWAYS manage !reserve regardless of other content in globalNotKeyword
        // Set global nots keyword - add !reserve at the end if 'reserve' isn't in any field
        if (hasReserveWord) {
            // Remove !reserve when reserve is found in any keyword
            let globalNots = updatedForm.globalNotKeyword || '';
            globalNots = globalNots.replace(/&?!reserve/g, '');
            // Clean up any double ampersands and leading/trailing ampersands
            globalNots = globalNots.replace(/&{2,}/g, '&').replace(/^&+|&+$/g, '');
            updatedForm.globalNotKeyword = globalNots;
        } else {
            // Ensure !reserve is at the end when no reserve word is found
            let globalNots = updatedForm.globalNotKeyword || '';
            globalNots = globalNots.replace(/&?!reserve/g, ''); // Remove existing !reserve
            // Clean up any double ampersands and leading/trailing ampersands
            globalNots = globalNots.replace(/&{2,}/g, '&').replace(/^&+|&+$/g, '');
            // Add !reserve at the end
            globalNots = globalNots ? `${globalNots}&!reserve` : '!reserve';
            updatedForm.globalNotKeyword = globalNots;
        }
        
        return updatedForm;
    };

    // Helper functions needed for generateKeywords
    const escapeRegex = (str: string) => str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const tokenize = (input: string) => input.split(/[^a-z0-9]+/).filter(Boolean);
    const applyAliases = (input: string, aliasMap: Record<string, string>) => {
    let result = input.toLowerCase();
    
    if (!aliasMap || Object.keys(aliasMap).length === 0) {
        return result;
    }
    
    // Create a flat array of all alias combinations with their replacement values
    const allAliases: Array<{ pattern: string, replacement: string, length: number }> = [];
    
    for (const [aliasKey, aliasValue] of Object.entries(aliasMap)) {
        if (!aliasKey || !aliasValue) continue;
        
        // Split the alias key by '|' to get all possible alias variations
        const aliasParts = aliasKey.split('|').map(k => k.trim()).filter(Boolean);
        
        for (const aliasPart of aliasParts) {
            if (!aliasPart) continue;
            
            // Calculate pattern length by word count and character count for better sorting
            const wordCount = aliasPart.split(/\s+/).filter(Boolean).length;
            const charCount = aliasPart.length;
            
            allAliases.push({
                pattern: aliasPart.toLowerCase(),
                replacement: aliasValue.toLowerCase(),
                length: wordCount * 1000 + charCount // Prioritize by word count, then character count
            });
        }
    }
    
    // Sort by length (word count first, then character count) - longest first
    allAliases.sort((a, b) => b.length - a.length);
    
    // Apply each alias pattern, ensuring longer matches are processed first
    for (const alias of allAliases) {
        const aliasWords = alias.pattern.split(/\s+/).filter(Boolean);
        const pattern = aliasWords.map(w => escapeRegex(w)).join('[^a-z0-9]*');
        const regex = new RegExp(`(^|[^a-z0-9])${pattern}($|[^a-z0-9])`, 'gi');
        
        result = result.replace(regex, (_, prefix, suffix) => {
            return prefix + alias.replacement + suffix;
        });
    }
    
    return result;
    };

    const removeEliminations = (tokens: string[], eliminations: string[]) => {
    if (!eliminations || eliminations.length === 0) {
        return tokens;
    }
    
    const phrases = eliminations
        .map(e => String(e).toLowerCase().trim())
        .filter(Boolean)
        .sort((a, b) => b.split(/\s+/).length - a.split(/\s+/).length || b.length - a.length)
        .map(p => p.split(/\s+/).filter(Boolean));

    let result: string[] = [];
    for (let i = 0; i < tokens.length;) {
        let matched = false;
        for (const phrase of phrases) {
        if (i + phrase.length <= tokens.length && 
            phrase.every((word, idx) => tokens[i + idx] === word)) {
            i += phrase.length;
            matched = true;
            break;
        }
        }
        if (!matched) {
        result.push(tokens[i]);
        i++;
        }
    }
    return result;
    };

    // =============================================================================
    // EVENT HANDLERS AND FORM PROCESSING
    // =============================================================================

    /**
     * Form Processing Group
     * These handlers manage form interactions and data processing
     * Core functionality is shared across all modes with mode-specific branches
     */

    /**
     * Handles form input changes
     * Processes input based on field type and current mode
     * Includes special handling for producer and wine fields
     */
    const handleWeightChange = (keywordType: keyof typeof keywordWeights, newValue: number) => {
        // Create the new weights object immediately
        const newWeights = {
            ...keywordWeights,
            [keywordType]: newValue
        };
        
        // Update the state
        setKeywordWeights(newWeights);
        
        // Regenerate main keyword using current keyword field values (which might be manually edited)
        const repeat = (val: string, times: number) => {
            if (!val) return '';
            return Array(times).fill(val).join('&');
        };

        // Use current form values (which may be manually edited) with new weights
        const keywordParts = [
            repeat(form.producerKeyword, newWeights.producerKeyword),
            repeat(form.baseKeyword, newWeights.baseKeyword),
            repeat(form.appellationKeyword, newWeights.appellationKeyword),
            repeat(form.colorKeyword, newWeights.colorKeyword),
            repeat(form.commonModifiersKeyword, newWeights.commonModifiersKeyword),
            repeat(form.modifiersKeyword, newWeights.modifiersKeyword)
        ].filter(Boolean);
        // Always add customNotKeyword with weight 1, just before globalNotKeyword
        if (form.customNotKeyword && form.customNotKeyword.trim()) {
            keywordParts.push(form.customNotKeyword.trim());
        }
        // Add global nots at the end if they exist
        if (form.globalNotKeyword && form.globalNotKeyword.trim()) {
            keywordParts.push(form.globalNotKeyword.trim());
        }

        const newKeyword = keywordParts
            .join('&')
            .replace(/&{2,}/g, '&')
            .replace(/^&+|&+$/g, '');

        // Update form with new keyword - don't modify globalNotKeyword here
        setForm(prev => ({
            ...prev,
            keyword: newKeyword
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        if (name === 'noiseMode' && type === 'checkbox') {
            setNoiseEnabled(checked);
        }
    
        let updatedForm = {
            ...form,
            [name]: type === 'checkbox' ? checked : value
        };

        // In edit mode, do NOT auto-generate keywords when editing labelName or producerShow
        if (editingWine && (name === 'producerShow' || name === 'labelName')) {
            setForm(updatedForm);
            return;
        }

        // Check if the changed field is a keyword field
        const isKeywordField = [
            'producerKeyword', 
            'baseKeyword', 
            'appellationKeyword', 
            'colorKeyword', 
            'commonModifiersKeyword', 
            'modifiersKeyword',
            'customNotKeyword',
            'globalNotKeyword'
        ].includes(name);

        // In non-edit mode, keep previous behavior for producerShow and labelName
        if (!editingWine && (name === 'producerShow' || name === 'labelName')) {
            updatedForm = generateKeywords(updatedForm, keywordWeights);
            // If both producer and label are empty, clear main keyword field
            if ((!updatedForm.producerShow || updatedForm.producerShow.trim() === '') &&
                (!updatedForm.labelName || updatedForm.labelName.trim() === '')) {
                updatedForm.keyword = '';
            }
            
            // Apply conflict removal logic for producer/label changes
            // This should work regardless of whether all transformation data is loaded
            if (isTransformationDataLoaded && (reservedWordsRedOpp || reservedWordsWhiteOpp || reservedWordsRoseOpp)) {
                updatedForm = applyConflictRemovalLogic(updatedForm);
                // Re-run generateKeywords to properly manage !reserve after conflict removal
                updatedForm = generateKeywords(updatedForm, keywordWeights);
            }
            
            // Apply new generateKeyword logic when producer/label changes (for full variety-specific transformations)
            // This ensures the transformation works regardless of the order of field changes
            
            if (isTransformationDataLoaded && varietiesFullData.length > 0 && colorsOptions.length > 0) {
                updatedForm = applyNewGenerateKeywordLogic(updatedForm);
                // Re-run generateKeywords to properly manage !reserve after color substitutions
                updatedForm = generateKeywords(updatedForm, keywordWeights);
            }
        } else if (isKeywordField) {
            // When any keyword field is manually edited, regenerate the main keyword
            // but don't regenerate the individual keyword fields - use the manually edited values
            
            // Helper function to repeat keywords based on weights
            const repeat = (val: string, times: number) => {
                if (!val) return '';
                return Array(times).fill(val).join('&');
            };

            // Check if "reserve" appears in any keyword field after this change
            const containsExactReserve = (str: string) =>
                str?.split('&').map(w => w.trim()).includes('reserve');

            const hasReserveWord = (
                containsExactReserve(updatedForm.producerKeyword) ||
                containsExactReserve(updatedForm.baseKeyword) ||
                containsExactReserve(updatedForm.appellationKeyword) ||
                containsExactReserve(updatedForm.colorKeyword) ||
                containsExactReserve(updatedForm.commonModifiersKeyword) ||
                containsExactReserve(updatedForm.modifiersKeyword)
            );

            // Generate main keyword from the manually edited keyword fields
            const keywordParts = [
                repeat(updatedForm.producerKeyword, keywordWeights.producerKeyword),
                repeat(updatedForm.baseKeyword, keywordWeights.baseKeyword),
                repeat(updatedForm.appellationKeyword, keywordWeights.appellationKeyword),
                repeat(updatedForm.colorKeyword, keywordWeights.colorKeyword),
                repeat(updatedForm.commonModifiersKeyword, keywordWeights.commonModifiersKeyword),
                repeat(updatedForm.modifiersKeyword, keywordWeights.modifiersKeyword)
            ].filter(Boolean);
            // Always add customNotKeyword with weight 1, just before globalNotKeyword
            if (updatedForm.customNotKeyword && updatedForm.customNotKeyword.trim()) {
                keywordParts.push(updatedForm.customNotKeyword.trim());
            }
            // Add global nots at the end if they exist
            if (updatedForm.globalNotKeyword && updatedForm.globalNotKeyword.trim()) {
                keywordParts.push(updatedForm.globalNotKeyword.trim());
            }

            updatedForm.keyword = keywordParts
                .join('&')
                .replace(/&{2,}/g, '&')
                .replace(/^&+|&+$/g, '');

            // Only auto-manage globalNotKeyword if the user is NOT directly editing it
            if (name !== 'globalNotKeyword') {
                // Update global nots keyword based on reserve detection
                if (hasReserveWord) {
                    // Remove !reserve when reserve is found in any keyword
                    let globalNots = updatedForm.globalNotKeyword || '';
                    globalNots = globalNots.replace(/&?!reserve/g, '');// Clean up any double ampersands and leading/trailing ampersands
                    globalNots = globalNots.replace(/&{2,}/g, '&').replace(/^&+|&+$/g, '');
                    updatedForm.globalNotKeyword = globalNots;
                } else {
                    // Ensure !reserve is at the end when no reserve word is found
                    let globalNots = updatedForm.globalNotKeyword || '';
                    globalNots = globalNots.replace(/&?!reserve/g, ''); // Remove existing !reserve
                    // Clean up any double ampersands and leading/trailing ampersands
                    globalNots = globalNots.replace(/&{2,}/g, '&').replace(/^&+|&+$/g, '');
                    // Add !reserve at the end
                    globalNots = globalNots ? `${globalNots}&!reserve` : '!reserve';
                    updatedForm.globalNotKeyword = globalNots;
                }
            }
            // Show keyword diff immediately in edit mode when any keyword field changes
            if (editingWine) {
                setOriginalKeyword(originalKeyword || '');
                setRegeneratedKeyword(updatedForm.keyword || '');
                setShowKeywordDiff(true);
            }
        } else {
            // For other fields, handle reserve logic as before
            // Use the same exact reserve detection method for consistency
            const containsExactReserve = (str: string) =>
                str?.split('&').map(w => w.trim()).includes('reserve');

            const hasReserveWord = (
                containsExactReserve(updatedForm.producerKeyword) ||
                containsExactReserve(updatedForm.baseKeyword) ||
                containsExactReserve(updatedForm.appellationKeyword) ||
                containsExactReserve(updatedForm.colorKeyword) ||
                containsExactReserve(updatedForm.commonModifiersKeyword) ||
                containsExactReserve(updatedForm.modifiersKeyword) ||
                (name.includes('keyword') && containsExactReserve(String(value)))
            );
            
            // Update global nots field based on reserve detection
            if (hasReserveWord) {
                // Remove !reserve when reserve is found in any keyword
                let globalNots = updatedForm.globalNotKeyword || '';
                globalNots = globalNots.replace(/&?!reserve/g, '');
                // Clean up any double ampersands and leading/trailing ampersands
                globalNots = globalNots.replace(/&{2,}/g, '&').replace(/^&+|&+$/g, '');
                updatedForm.globalNotKeyword = globalNots;
            } else if (updatedForm.keyword) { // Only add !reserve if we have a keyword
                // Ensure !reserve is at the end when no reserve word is found
                let globalNots = updatedForm.globalNotKeyword || '';
                globalNots = globalNots.replace(/&?!reserve/g, ''); // Remove existing !reserve
                // Clean up any double ampersands and leading/trailing ampersands
                globalNots = globalNots.replace(/&{2,}/g, '&').replace(/^&+|&+$/g, '');
                // Add !reserve at the end
                globalNots = globalNots ? `${globalNots}&!reserve` : '!reserve';
                updatedForm.globalNotKeyword = globalNots;
            }
        }
        
        setForm(updatedForm);
    };
    
    /**
     * Handles multiselect input changes
     */
    const handleMultiSelectChange = (name: string, values: string[]): void => {
        setForm(prev => ({
            ...prev,
            [name]: values
        }));
    };

    /**
     * Handles wine row selection in the table
     */
    const handleWineRowSelect = (row: any, checked: boolean): void => {
        setSelectedWineRows(prev => {
            if (checked) {
                if (prev.some(r => r.id === row.id)) return prev;
                if (prev.length >= 3) {
                    toast.error('Please select at most 3 wines for merging');
                    return prev;
                }
                return [...prev, row];
            } else {
                return prev.filter(r => r.id !== row.id);
            }
        });
    };

    /**
     * Handles clicking on a wine name in the table
     * If noiseMode is ON, add to noises as before.
     * If noiseMode is OFF and label field is focused, append to labelName with a space.
     */
    const handleWineNameClick = (wineName: string): void => {
        if (form.noiseMode) {
            // Existing behavior: add to noises
            const words = wineName.split(/\s+/).filter(Boolean);
            setForm(prev => {
                const newWords = words.filter(word => !prev.noises.includes(word));
                return {
                    ...prev,
                    noises: [...prev.noises, ...newWords]
                };
            });
        } else if (isLabelFieldFocused) {
            // Cancel blur timeout so label stays virtually focused
            if (labelBlurTimeoutRef.current) {
                clearTimeout(labelBlurTimeoutRef.current);
                labelBlurTimeoutRef.current = null;
            }
            setIsLabelFieldFocused(true);
            setForm(prev => ({
                ...prev,
                labelName: prev.labelName ? prev.labelName.trim() + ' ' + wineName.trim() : wineName.trim()
            }));
        }
    };

    /**
     * Handles clicking on a word in the label field
     */
    const handleLabelWordClick = (word: string): void => {
        setForm(prev => {
            if (prev.noises.includes(word)) return prev;
            return {
                ...prev,
                noises: [...prev.noises, word]
            };
        });
    };

    /**
     * Handles form submission
     */
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    /**
     * Handles navigation back to validation page with persisted state
     */
    const navigateBackToValidation = () => {
        console.log('NavigateBackToValidation called:', {
            isFromNarrow,
            hasLocationState: !!location.state,
            hasRestoreNarrowState: !!location.state?.restoreNarrowState,
            locationStateKeys: location.state ? Object.keys(location.state) : []
        });

        // If coming from narrow, restore all relevant state
        if (isFromNarrow && location.state?.restoreNarrowState) {
            console.log('Navigating back to narrow with state:', location.state.restoreNarrowState);
            // Pass the narrow state exactly as narrow page expects it
            navigate('/narrow', {
                state: { 
                    restoreNarrowState: location.state.restoreNarrowState 
                }
            });
            return;
        }
        // Enhanced: restore validation mode and search state if present
        const validationState = (location.state as any)?.validationState || {};
        
        // If coming from validation, restore all relevant state including narrow mode
        if (Object.keys(validationState).length > 0) {
            navigate('/validation', {
                state: {
                    validationState: validationState
                }
            });
        } else {
            // Fallback to normal back navigation
            navigate(-1);
        }
    };

    /**
     * Handles form submission based on current mode:
     * 1. New Producer Mode: Creates both producer and wine
     * 2. Add Wine Mode: Creates wine with existing producer
     * 3. Edit Wine Mode: Updates existing wine
     */
    const onSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            let producerPayload;
            let res1;

            // Only create producer if not hiding producer fields
            if (!shouldHideProducerFields) {
                const producerLiteralRes = await getLiteralforProducer({ reg_code: form.regionCode });
                const literalByRegCodeResult = producerLiteralRes?.data?.data || '';
                producerPayload = {
                    literal: literalByRegCodeResult,
                    producer_name: form.producerName,
                    producer_show: form.producerShow,
                    producer_noise: form.producerNoises,
                    producer_keyword: form.producerKeyword,
                };
                res1 = await createProducer(producerPayload);
            }

            // Build noise array from the noises field in the form state
            let noiseWords: string[] = form.noises || [];

            // Get external wine data for vintage, wine_n_id, and mongo_id if available
            const contextData = (isFromExternalInput && dataLoaded && externalTableData && externalTableData[externalCurrentIndex]) 
                ? externalTableData[externalCurrentIndex] 
                : null;
                
            const locationData = (location.state as any)?.externalWineData;
            
            // Prioritize context data over location data
            const externalData = contextData || locationData || {};

            const rawPayload = {
                producer_id: shouldHideProducerFields ? producerIdFromRoute : res1?.data?.data?.id,
                literal: shouldHideProducerFields ? literalByRegCodeResult : form.nextId,
                label_name: form.labelName,
                variety: parseInt(form.variety as any) || 0,
                color: form.color,
                sweetness: form.sweetness,
                type: form.wineType,
                country: parseInt(form.country as any) || 0,
                region: parseInt(form.region as any) || 0,
                sub_region: parseInt(form.sub_region as any) || 0,
                appellation: parseInt(form.appellations as any) || 0,
                sub_appellation: parseInt(form.sub_appellations as any) || 0,
                // all_appellations_tree: form.all_appellations_tree,
                noise: noiseWords,
                producer_keyword: form.producerKeyword,
                base_keyword: form.baseKeyword,
                appellation_keyword: form.appellationKeyword,
                color_keyword: form.colorKeyword,
                common_modifiers: form.commonModifiersKeyword,
                modifiers: form.modifiersKeyword,
                custom_nots: form.customNotKeyword,
                global_nots: form.globalNotKeyword,
                keyword: form.keyword,
                // Add new fields from external data if available
                vintage: externalData?.vintage || "",
                wine_n_id: externalData?.wine_vinid || externalData?.wine_n_id || "",
                mongo_id: externalData?.literal || externalData?.mongo_id || ""
            };

            // Remove empty fields (null, undefined, empty string, empty array, 0 for ids)
            const payload = Object.fromEntries(
                Object.entries(rawPayload).filter(([key, value]) => {
                    if (value === null || value === undefined) return false;
                    if (typeof value === 'string' && value.trim() === '') return false;
                    if (Array.isArray(value) && value.length === 0) return false;
                    // For id fields, allow 0 only if it's a valid id (e.g., variety, country, etc.)
                    // If you want to skip 0 for ids, uncomment below:
                    // if (typeof value === 'number' && value === 0) return false;
                    return true;
                })
            );

            if (editingWine && routeProducerId) {
                // Update wine logic
                let res = [];
                if(fetchedWineId) {
                    res = await updateWine( fetchedWineId, payload as any);
                    setFetchedWineId(null); // reset after use
                } else {
                    res = await updateWine( routeProducerId, payload as any);
                }
                toast.success(res?.data?.message || 'Wine updated successfully!');
                navigateBackToValidation();
            } else {
                // Create wine logic
                const res = await createWine(payload as any);
                toast.success(res?.data?.message || 'Wine added successfully!');
                navigateBackToValidation();
            }
        } catch (err: any) {
            toast.error(err || err?.message || (editingWine ? 'Failed to update wine.' : 'Failed to create wine.'));
        } finally {
            setSubmitting(false);
        }
    };

    // =============================================================================
    // UTILITY AND HELPER FUNCTIONS
    // =============================================================================

    /**
     * UI Helper Functions
     * These functions support the UI rendering and user experience
     * They handle mode-specific UI elements and interactions
     */

    /**
     * Gets the appropriate breadcrumb title based on current mode:
     * - Edit Wine Mode: "Edit Wine" (unified for both direct input and external input)
     * - Add Wine Mode: "Add Wine to Producer" 
     * - New Producer Mode: "Add New Wine Producer"
     * @returns {string} The appropriate title for the current mode
     */
    const getBreadcrumbTitle = (): string => {
        if (editingWine) {
            return "Edit Wine";
        }
        if (shouldHideProducerFields) {
            return "Add Wine to Producer";
        }
        return "Add New Wine Producer";
    };

    /**
     * Gets the selected region code value for the dropdown (memoized)
     */
    const getSelectedRegionCode = React.useCallback((): string => {
        if (shouldHideProducerFields) {
            // console.log("regFromLiteral:", regFromLiteral, "regCodes:", regCodes);
            if (!regFromLiteral) return '';
            const matched = regCodes.find(code => code.value.startsWith(regFromLiteral));
            return matched ? matched.value : '';
        }
        return form.regionCode;
    }, [shouldHideProducerFields, regFromLiteral, regCodes, form.regionCode]);

    // Memoize the selected region code value to avoid repeated calls
    const selectedRegionCode = React.useMemo(() => getSelectedRegionCode(), [getSelectedRegionCode]);


    // =============================================================================
    // LIFECYCLE AND EFFECT MANAGEMENT
    // =============================================================================

    /**
     * Effect Groups
     * Organized by functionality and mode-specific behaviors
     */

    /**
     * Mode Setup Effects
     * Handle initialization and setup for each operating mode
     */

    /**
     * Edit Mode Setup Effect
     * - Now managed by the improved effect above that only runs once
     * - We've removed the unconditional call that was causing the double fetch
     */


    /**
     * Effect to populate form data from external input when in external input mode
     * Ensures the form fields are populated with external data after dropdowns are loaded
     */
    useEffect(() => {
        // Don't run if editing wine or dropdowns not loaded
        if (editingWine || !dropdownsLoaded) return;
        
        // Check both data sources
        const contextData = (fromExternalInput && dataLoaded && externalTableData && externalTableData[externalCurrentIndex]) 
            ? externalTableData[externalCurrentIndex] 
            : null;
            
        const locationData = (location.state as any)?.externalWineData;
        
        // Prioritize context data over location data
        const externalData = contextData || locationData;
        
        if (externalData) {
            
            // Function to find dropdown option ID by label
            const findOptionId = (options: OptionType[], label: string): number => {
                if (!label) return 0;
                // Try exact match first
                let option = options.find(opt => 
                    opt.label && opt.label.toLowerCase() === String(label).toLowerCase()
                );
                
                // If no match, try partial match
                if (!option && label) {
                    option = options.find(opt => 
                        opt.label && opt.label.toLowerCase().includes(String(label).toLowerCase())
                    );
                }
                
                return option ? Number(option.value) : 0;
            };

            let matchedRegCode = '';
            if (externalData.region_code) {
                const regCodeValue = externalData.region_code;
                // Try exact match first, then startsWith match
                const matched = regCodes.find(code => 
                    code.value.toLowerCase() === regCodeValue.toLowerCase() ||
                    code.value.toLowerCase().startsWith(regCodeValue.toLowerCase())
                );
                matchedRegCode = matched ? matched.value : '';

                // Only for +wine mode from external input, override regFromLiteral with external region code
                if (shouldHideProducerFields && isFromExternalInput) {
                    setRegFromLiteral(matchedRegCode);
                }
                
                // Update form with matched region code only for new wine or non-+wine mode
                if (!shouldHideProducerFields) {
                    setForm(prev => ({
                        ...prev,
                        regionCode: matchedRegCode || prev.regionCode
                    }));

                    // If we have a region code and not in +wine mode, call getLiteral API
                    if (matchedRegCode) {
                        const fetchLiteralAndUpdateForm = async () => {
                            try {
                                const literalRes = await getLiteral({ reg_code: matchedRegCode });
                                let wineId = literalRes?.data?.data?.wineId || literalRes?.data?.wineId || literalRes?.data?.data || '';

                                if (externalData.vintage && String(externalData.vintage).length === 4) {
                                    const formattedVintage = String(externalData.vintage)[0] + String(externalData.vintage).slice(2);
                                    wineId = `${wineId}${formattedVintage}`;
                                }

                                setForm(prev => ({
                                    ...prev,
                                    nextId: wineId
                                }));
                            } catch (err : any) {
                                toast.error(err);
                            }
                        };

                        fetchLiteralAndUpdateForm();
                    }
                }
            }
            
            // Update form values from external data - ONLY use label for labelName
            setForm(prev => ({
                ...prev,
                // Basic wine details - ONLY use label for label name
                labelName: externalData.label || prev.labelName, // STRICT: Only use label field for labelName
                regionCode: matchedRegCode || prev.regionCode,
                
                // Producer data (only for "Add Wine New Producer" mode)
                producerName: !shouldHideProducerFields ? (externalData.producerName || externalData.producer_name || prev.producerName) : prev.producerName,
                producerShow: !shouldHideProducerFields ? (externalData.producerShow || externalData.producer_name_show || prev.producerShow) : prev.producerShow,
                
                // Wine properties
                color: externalData.color || prev.color,
                sweetness: externalData.sweetness || prev.sweetness,
                wineType: externalData.wine_type || externalData.wine_type || prev.wineType,
                
                // Location fields - find matching dropdown options by label
                country: findOptionId(countryOptions, externalData.country || externalData.wine_country),
                region: findOptionId(regionOptions, externalData.region || externalData.wine_region),
                sub_region: findOptionId(subRegionOptions, externalData.sub_region || externalData.location || externalData.subregion),
                appellations: findOptionId(appellationsOptions, externalData.locale || externalData.appellation),
                sub_appellations: findOptionId(subAppellationsOptions, externalData.site || externalData.sub_appellation),
                
                // Other fields
                variety: findOptionId(varietiesOptions, externalData.variety),
                
                // Enable noise mode
                noiseMode: true,

                nextId: prev.nextId,

            }));
            
            // // After form is updated with region code, fetch related data
            // if (matchedRegCode) {
            //     console.log('Running fetchSpreadTables and fetchWineId with regCode:', matchedRegCode);
            //     setTimeout(() => {
            //         fetchSpreadTables();
            //         fetchWineId();
            //     }, 500); // Small delay to ensure form update completes first
            // }
            if (matchedRegCode) {
              fetchSpreadTables();
            }

        }
    }, [
        fromExternalInput, 
        dataLoaded, 
        externalTableData, 
        externalCurrentIndex,
        countryOptions,
        regCodes,
        subRegionOptions,
        appellationsOptions,
        subAppellationsOptions,
        varietiesOptions,
        shouldHideProducerFields,
        editingWine,
        dropdownsLoaded,
        location.state,
        editingWine,
    ]);

    /**
     * Producer Mode Setup Effect
     * - Only runs when NOT in Edit Mode
     * - Sets up producer context for Add Wine Mode
     */

    /**
     * Initialize producer data when component mounts, but only if not editing a wine
     */
    useEffect(() => {
        if (!location.state?.fromEditButton) {
            fetchAndStoreProducer();
        }
    }, [location.state?.fromEditButton]);

    /**
     * Fetch literal by reg code when regFromLiteral changes
     */
    useEffect(() => {
        
        // Handle edit mode
        if (editingWine && regFromLiteral) {
            const matched = regCodes.find(code => code.value.startsWith(regFromLiteral));
            setLiteralByRegCodeResult(matched ? matched.value : '');
            return;
        }
        
        // Handle external input +wine mode
        if (shouldHideProducerFields && isFromExternalInput && regFromLiteral && literalOfProducer) {
            const fetchLiteralDirectly = async () => {
                try {
                    const params = {
                        reg_code: regFromLiteral,
                        producer_literal: literalOfProducer
                    };
                    const res = await getLiteral(params);
                    let wineId = res?.data?.data ? String(res.data.data) : '';

                    // Get the external data
                    const contextData = externalTableData?.[externalCurrentIndex];
                    const locationData = (location.state as any)?.externalWineData;
                    const externalData = contextData || locationData;

                    // Append vintage if available
                    if (externalData?.vintage && String(externalData.vintage).length === 4) {
                        const formattedVintage = String(externalData.vintage)[0] + String(externalData.vintage).slice(2);
                        wineId = `${wineId}${formattedVintage}`;
                    }

                    setLiteralByRegCodeResult(wineId);
                } catch (err : any) {
                    toast.error(err);
                    setLiteralByRegCodeResult('');
                }
            };
            fetchLiteralDirectly();
        }
        // Handle validation +wine mode
        else if (shouldHideProducerFields && isFromValidation && regFromLiteral && literalOfProducer) {
            const fetchLiteralDirectly = async () => {
                try {
                    const params = {
                        reg_code: regFromLiteral,
                        producer_literal: literalOfProducer
                    };
                    const res = await getLiteral(params);
                    let wineId = res?.data?.data ? String(res.data.data) : '';

                    // Get the validation data
                    const validationData = (location.state as any)?.validationWineData;

                    // Append vintage if available
                    if (validationData?.vintage && String(validationData.vintage).length === 4) {
                        const formattedVintage = String(validationData.vintage)[0] + String(validationData.vintage).slice(2);
                        wineId = `${wineId}${formattedVintage}`;
                    }

                    setLiteralByRegCodeResult(wineId);
                } catch (err : any) {
                    toast.error(err);
                    setLiteralByRegCodeResult('');
                }
            };
            fetchLiteralDirectly();
        }
        // Handle regular +wine mode (not from external)
        else if (regFromLiteral && literalOfProducer && !editingWine && !isFromExternalInput) {
            fetchLiteralByRegCode();
        }
    }, [editingWine, regFromLiteral, literalOfProducer, regCodes, isFromExternalInput, shouldHideProducerFields, externalTableData, externalCurrentIndex]);

    /**
     * Fetch wine ID when region code changes
     */
    useEffect(() => {
        fetchSpreadTables();
        fetchWineId();
    }, [form.regionCode]);
    
    /**
     * Fetch spread tables when regFromLiteral changes
     * This ensures that when in +wine mode or edit mode where regFromLiteral is set,
     * but form.regionCode isn't directly updated, we still fetch the spread tables
     */
    const processedRegCodeRef = React.useRef<string | null>(null);
    
    useEffect(() => {
        if (regFromLiteral && shouldHideProducerFields) {
            // Avoid processing the same regFromLiteral multiple times
            if (processedRegCodeRef.current === regFromLiteral) {
                return;
            }
            
            processedRegCodeRef.current = regFromLiteral;
            
            // Allow some time for the component to initialize before fetching
            const timer = setTimeout(() => {
                fetchSpreadTables();
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [regFromLiteral, shouldHideProducerFields]);

    /**
     * Initialize all dropdown options on component mount
     */
    useEffect(() => {
        const initializeDropdownOptions = async (): Promise<void> => {
            try {
                setIsInitializing(true);
                
                // Fetch all dropdown data in parallel
                await Promise.all([
                    fetchCountries(),
                    fetchRegCodes(),
                    fetchColors(),
                    fetchSweetness(),
                    fetchTypes(),
                    fetchVarieties(),
                    fetchAppellations(),
                    fetchSubAppellations(),
                    fetchRegions(),         
                    fetchSubRegions(),
                    fetchAppellationTree(),
                ]);
                
                await fetchNoise();
                setDropdownsLoaded(true);
            } catch (error) {
                console.error("Error initializing dropdown options:", error);
            } finally {
                setIsInitializing(false);
            }
        };
        
        // Only initialize dropdowns once
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            initializeDropdownOptions();
        }
    }, []);
    
    /**
     * Fetch keyword transformation data when component mounts
     * This ensures transformations work across all modes and scenarios
     */
    useEffect(() => {
        // Fetch keyword transformation data regardless of mode
        fetchKeywordTransformations();
    }, []);

    /**
     * Improved effect for fetchWineAndPopulate to ensure it's only called once
     */
    useEffect(() => {
        // Only run this effect if:
        // 1. We're in edit mode (location.state?.fromEditButton is true)
        // 2. We have a wine ID to fetch (routeProducerId exists)
        // 3. All dropdown options are loaded (dropdownsLoaded is true)
        // 4. We haven't already fetched the wine data (wineDataFetched is false)
        // 5. We're not still initializing (isInitializing is false)
        const shouldFetchWineData = 
            location.state?.fromEditButton && 
            routeProducerId && 
            dropdownsLoaded && 
            !wineDataFetched && 
            !isInitializing;
            
        if (shouldFetchWineData) {
            const fetchWineAndPopulate = async () => {
                try {
                    setEditingWine(true);
                    let wineRes;
                    if(routeProducerId.length === 9) {
                        wineRes = await getWineByLiteral(routeProducerId);
                        setFetchedWineId(wineRes?.data?.data?.id || null);
                    } else {
                        wineRes = await getWineById(routeProducerId);
                    }
                    const wineData = wineRes?.data?.data;
                    // Helper function to find option by label
                    const findOptionByLabel = (options: OptionType[], searchLabel: string): OptionType | undefined => {
                        if (!searchLabel) return undefined;
                        return options.find(opt => 
                            opt.label.toLowerCase() === searchLabel.toLowerCase()
                        );
                    };
                    
                    // Helper function to find option ID by label
                    const findOptionId = (options: OptionType[], labelOrId: string): number => {
                        if (!labelOrId) return 0;
                        // Try exact match by label
                        let option = options.find(opt =>
                            opt.label && opt.label.toLowerCase() === String(labelOrId).toLowerCase()
                        );
                        // If no match, try partial label match
                        if (!option) {
                            option = options.find(opt =>
                                opt.label && opt.label.toLowerCase().includes(String(labelOrId).toLowerCase())
                            );
                        }
                        if (!option) {
                            option = options.find(opt =>
                                String(opt.value) === String(labelOrId)
                            );
                        }
                        return option ? Number(option.value) : 0;
                    };
                    
                    // Compute modified producer field
                    const modProducerField = wineData?.producer?.name && wineData?.producer?.literal
                        ? `${wineData.producer.literal}-${wineData.producer.name}`
                        : undefined;
                                        
                    setProducerFetchedFromWineId(wineData?.producer?.literal || '');
                    setEditingWineLiteral(wineData?.literal || '');
                    
                    const fetchedCode = wineData?.producer?.literal?.substring(0, 2) || '';
                    // Set regFromLiteral to ensure region code gets selected in edit mode
                    setRegFromLiteral(fetchedCode);
                    
                    if (wineData) {
                        // Map wine data to form values, ensuring we find matching options by label
                        setForm(prev => {
                            const noisesArr = Array.isArray(wineData.wine_noises) 
                                ? wineData.wine_noises.map((n: any) => n.noise) 
                                : prev.noises;
                                
                            return {
                                ...prev,
                                regionCode: fetchedCode || prev.regionCode,
                                literal: wineData.literal || prev.literal,
                                nextId: modProducerField || prev.nextId,
                                labelName: wineData.name || prev.labelName,
                                color: wineData.color || prev.color,
                                wineType: wineData.type || prev.wineType,
                                sweetness: wineData.sweetness || prev.sweetness,
                                note: wineData.note || prev.note,
                                
                                // Find matching option IDs for select fields
                                country: findOptionId(countryOptions, wineData.wine_country),
                                region: findOptionId(regionOptions, wineData.wine_region),
                                sub_region: findOptionId(subRegionOptions, wineData.wine_location),
                                sub_appellations: findOptionId(subAppellationsOptions, wineData.wine_site),
                                all_appellations_tree: wineData.wine_site || prev.all_appellations_tree,
                                appellations: findOptionId(appellationsOptions, wineData.wine_locale),
                                
                                variety: wineData.variety_id || prev.variety,
                                noises: noisesArr,
                                noiseMode: Array.isArray(noisesArr) && noisesArr.length > 0 ? true : prev.noiseMode,
                                
                                // Producer data
                                producerNoises: Array.isArray(wineData.producer?.producer_noises) 
                                    ? wineData.producer.producer_noises.map((n: any) => n.noise) 
                                    : prev.producerNoises,
                                producerKeyword: wineData.producer?.producer_keyword?.keyword || prev.producerKeyword,
                                
                                // Keyword data
                                baseKeyword: wineData.wine_keyword?.base_keyword || prev.baseKeyword,
                                appellationKeyword: wineData.wine_keyword?.appellation_keyword || prev.appellationKeyword,
                                colorKeyword: wineData.wine_keyword?.color_keyword || prev.colorKeyword,
                                commonModifiersKeyword: wineData.wine_keyword?.common_modifiers_keyword || prev.commonModifiersKeyword,
                                modifiersKeyword: wineData.wine_keyword?.modifiers_keyword || prev.modifiersKeyword,
                                customNotKeyword: wineData.wine_keyword?.custom_not_keyword || prev.customNotKeyword,
                                globalNotKeyword: wineData.wine_keyword?.global_not_keyword || prev.globalNotKeyword,
                                
                                // Other fields
                                producerName: wineData.producer?.name || prev.producerName,
                                producerShow: wineData.producer?.name_show || prev.producerShow,
                                producerLiteral: wineData.producer?.literal || prev.producerLiteral,
                                producerId: wineData.producer?.id || prev.producerId,
                                filter_id: wineData.filter_id || prev.filter_id,
                                search_partialProducer: wineData.search_partialProducer ?? prev.search_partialProducer,
                                search_partial: wineData.search_partial ?? prev.search_partial,
                                table: wineData.table || prev.table,
                                keyword: wineData.wine_keyword?.pattern || prev.keyword
                            };
                        });
                        setOriginalKeyword(wineData.wine_keyword?.pattern || '');
                    }
                } catch (err : any) {
                    toast.error(err);
                } finally {
                    setWineDataFetched(true);
                }
            };
            
            fetchWineAndPopulate();
        }
    }, [location.state?.fromEditButton, routeProducerId, dropdownsLoaded, wineDataFetched, isInitializing, countryOptions, regionOptions, subRegionOptions, appellationsOptions, subAppellationsOptions]);

    /**
     * Reset removed noise words when labelName or noiseEnabled changes
     */
    useEffect(() => {
        setRemovedNoiseWords([]);
    }, [form.labelName, noiseEnabled]);

    /**
     * Handle document click to manage label field focus
     */
    useEffect(() => {
        function handleDocumentClick(e: MouseEvent) {
            const inputWrapper = labelInputWrapperRef.current;
            const table = wineTableRef.current;
            if (!inputWrapper) return;
            const target = e.target as Node;
            // If click is inside input wrapper or table, do nothing
            if (inputWrapper.contains(target) || (table && table.contains(target))) {
                return;
            }
            // Otherwise, blur virtual focus
            setIsLabelFieldFocused(false);
        }
        if (isLabelFieldFocused) {
            document.addEventListener('mousedown', handleDocumentClick);
        }
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [isLabelFieldFocused]);

    /**
     * Fetch wines for table when producerFetchedFromWineId or search_partial changes
     */
    useEffect(() => {
        if (producerFetchedFromWineId) {
            fetchWinesForTable(producerFetchedFromWineId, 1, false);
        } else {
            // Clear wine table data if no producer is set
            setWineTableData([]);
            setHasMoreWineTableData(true);
            setWineTablePage(1);
        }
    }, [producerFetchedFromWineId, form.search_partial]);

    // Add new useEffect to trigger keyword generation when data is loaded from external sources
    // ONLY runs for NEW wine creation (both direct input and external input), NEVER in edit mode
    useEffect(() => {
    // Only run after external data is loaded and transformations are ready
    // Prevent keyword generation in edit mode to preserve API-fetched data
    if (!editingWine && !isInitializing && 
        dropdownsLoaded && 
        isTransformationDataLoaded && 
        spreadTablesLoaded && 
        (form.labelName || form.producerShow)) {
        let updatedForm = generateKeywords(form, keywordWeights);
        // Apply variety-specific transformations if variety is selected
        if (form.variety && form.variety !== 0 && colorsOptions.length > 0) {
            updatedForm = applyNewGenerateKeywordLogic(updatedForm);
            // Re-run generateKeywords to properly manage !reserve after color substitutions
            updatedForm = generateKeywords(updatedForm, keywordWeights);
        }
        setForm(updatedForm);
    }
    }, [
    editingWine,
    isInitializing, 
    dropdownsLoaded, 
    isTransformationDataLoaded, 
    spreadTablesLoaded, 
    form.labelName, 
    form.producerShow,
    form.variety, // Add form.variety to dependencies
    colorsOptions.length, // Add colorsOptions dependency
    keywordWeights
    ]);

    // Add another useEffect to mark spread tables as loaded
    useEffect(() => {
    if (spreadTables && Object.keys(spreadTables).length > 0) {
        setSpreadTablesLoaded(true);
    }
    }, [spreadTables]);

    // // For edit wine mode, regenerate keywords after wine data is fetched
    // useEffect(() => {
    // if (wineDataFetched && editingWine && form.labelName) {
    //     console.log('Regenerating keywords for editing wine');
    //     const updatedForm = generateKeywords(form, keywordWeights);
    //     setForm(updatedForm);
    // }
    // }, [wineDataFetched, editingWine, form.labelName, keywordWeights]);

    // For external input mode, trigger keyword generation when external data is loaded
    // ONLY for NEW wine creation, NOT for editing existing wines
    useEffect(() => {
    if (!editingWine && isFromExternalInput && form.labelName && spreadTablesLoaded && isTransformationDataLoaded) {
        let updatedForm = generateKeywords(form, keywordWeights);
        // Apply variety-specific transformations if variety is selected
        if (form.variety && form.variety !== 0 && colorsOptions.length > 0) {
            updatedForm = applyNewGenerateKeywordLogic(updatedForm);
            // Re-run generateKeywords to properly manage !reserve after color substitutions
            updatedForm = generateKeywords(updatedForm, keywordWeights);
        }
        setForm(updatedForm);
    }
    }, [
    editingWine,
    isFromExternalInput, 
    form.labelName, 
    form.variety, // Add form.variety to dependencies
    colorsOptions.length, // Add colorsOptions dependency
    spreadTablesLoaded, 
    isTransformationDataLoaded,
    keywordWeights
    ]);

    //update noise list if labelname changes
    useEffect(() => {
        // Only run when noiseMode is OFF
        if (!form.noiseMode) {
            const labelWords = new Set(form.labelName.split(/\s+/).filter(Boolean));
            setForm(prev => ({
                ...prev,
                noises: prev.noises.filter(word => labelWords.has(word))
            }));
        }
    }, [form.labelName, form.noiseMode]);

    // =============================================================================
    // Additional External Input Effect
    // =============================================================================
    
    /**
     * External Input Effect
     * - Updates form when external wine data changes
     */
    // useEffect(() => {
    // // Check for external wine data in location state
    //     const externalWineData = (location.state as any)?.externalWineData;
        
    //     if (isFromExternalInput && externalWineData && !editingWine) {  // Added !editingWine check
    //         console.log("Updating form from external wine data:", externalWineData);
            
    //         // Update form with external wine data
    //         if (externalWineData.literal) {
    //             setForm(prev => ({
    //                 ...prev,
    //                 labelName: externalWineData.literal,
    //                 literal: externalWineData.literal
    //             }));
    //         }
            
    //         // Add any other fields from externalWineData that should update the form
    //     }
    // }, [isFromExternalInput, location.state, editingWine]); // Added editingWine to dependency array

    

    // =============================================================================
    // RENDER LOGIC AND UI COMPOSITION
    // =============================================================================

    /**
     * UI Component Assembly
     * Handles the rendering logic for all three modes:
     * - New Producer Mode
     * - Add Wine Mode
     * - Edit Wine Mode
     */

    const keywordsList = [
        form.baseKeyword,
        form.appellationKeyword,
        form.colorKeyword,
        form.commonModifiersKeyword,
        form.modifiersKeyword
    ].filter(Boolean);

    const filter_id = form.filter_id;

    const lockedProducerLiterals = shouldHideProducerFields && literalOfProducer ? [literalOfProducer] : [];

    const tableProps = React.useMemo(() => ({
        keywordsList,
        filter_id,
        lockedProducerLiterals
    }), [
        form.baseKeyword,
        form.appellationKeyword,
        form.colorKeyword,
        form.commonModifiersKeyword,
        form.modifiersKeyword,
        form.filter_id,
        literalOfProducer,
        shouldHideProducerFields
    ]);

    const labelInputWrapperRef = React.useRef<HTMLDivElement | null>(null);
    const wineTableRef = React.useRef<HTMLDivElement | null>(null);

    return (
        <div id='newProducerPage' className='main-content-box'>
            <BreadCrumb title={getBreadcrumbTitle()} />
            <div className="content-packed">
                {isInitializing && (
                    <div className="flex items-center justify-center p-8">
                        <div className="text-center">
                            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-red-600 border-r-transparent"></div>
                            <p className="mt-2 text-sm text-gray-600">Loading form data...</p>
                        </div>
                    </div>
                )}

                {!isInitializing && (
                    <form onSubmit={onSubmit} onKeyDown={handleKeyDown}>
                        <div className="flex-1 overflow-y-none">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-4">

                                {/* New Wine Display Row */}
                                {!editingWine ? (
                                    <div className="grid grid-cols-2 gap-3 py-2 mb-6">
                                        <div className="font-bold text-left">New Wine</div>
                                        <div className="text-right">
                                            {shouldHideProducerFields ? literalByRegCodeResult : form.nextId}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3 py-2 mb-6">
                                        <div className="font-bold text-left">Editing Wine</div>
                                        <div className="text-right">
                                            {editingWineLiteral || ' '}
                                        </div>
                                    </div>
                                )}

                                {/* Add ExternalWineInfoTable when fromExternalInput and dataLoaded are true */}
                                {/* Add ExternalWineInfoTable when fromExternalInput and dataLoaded are true */}
                                {isFromExternalInput && isDataLoaded && !editingWine && (
                                    <ExternalWineInfoTable
                                        mongoId={
                                            // Check both context and location state
                                            (externalTableData && externalTableData[externalCurrentIndex]?.literal) || 
                                            (location.state?.externalWineData?.literal) ||
                                            ''
                                        }
                                        wineVinId={
                                            (externalTableData && externalTableData[externalCurrentIndex]?.wine_vinid) || 
                                            (location.state?.externalWineData?.wine_vinid) ||
                                            ''
                                        }
                                        vintage={
                                            (externalTableData && externalTableData[externalCurrentIndex]?.vintage) || 
                                            (location.state?.externalWineData?.vintage) ||
                                            ''
                                        }
                                        fromExternalInput={isFromExternalInput}
                                    />
                                )}

                                {/* Add ValidationWineInfoTable when fromValidation and dataLoaded are true */}
                                {isFromValidation && isValidationDataLoaded && !editingWine && (
                                    <ValidationWineInfoTable
                                        retailerDescription={
                                            (location.state?.validationWineData?.description) || ''
                                        }
                                        vintage={
                                            (location.state?.validationWineData?.vintage) || ''
                                        }
                                        fromValidation={isFromValidation}
                                    />
                                )}
                                {/* Show ValidationWineInfoTable when fromNarrow and narrowWineData is present */}
                                {isFromNarrow && narrowWineData && !editingWine && (
                                    <ValidationWineInfoTable
                                        retailerDescription={narrowWineData.description || ''}
                                        vintage={narrowWineData.vintage || ''}
                                        fromValidation={false}
                                    />
                                )}
                                {/* Reg Code */}
                                <div className="grid grid-cols-2 gap-3">
                                <SearchableFloatingSelect
                                    label="Reg Code"
                                    name="regionCode"
                                    value={selectedRegionCode ? {
                                    label: regCodes.find(rc => String(rc.value) === String(selectedRegionCode))?.label || '',
                                    value: String(selectedRegionCode)
                                    } : null}
                                    options={regCodes}
                                    onChange={(option) => {
                                    setForm(prev => ({
                                        ...prev,
                                        regionCode: option ? option.value : ''
                                    }));
                                    }}
                                    error=""
                                    disabled={shouldHideProducerFields}
                                    className='searchableFloatingSelect'
                                />
                                {/* ...existing code for Wine ID/Producer ... */}
                                {!shouldHideProducerFields ? (
                                    <InputGroup
                                        type="text"
                                        label="Wine ID"
                                        name="nextId"
                                        value={form.nextId}
                                        handleChange={handleChange}
                                        inputVariant="floating"
                                        disabled
                                        className='newProducerInputGroup'
                                    />
                                ) : editingWine ? (
                                    <InputGroup
                                        type="text"
                                        label="Producer"
                                        name="producer"
                                        value={
                                            form.producerLiteral && form.producerShow
                                                ? `${form.producerLiteral}-${form.producerShow}`
                                                : ""
                                        }
                                        handleChange={handleChange}
                                        inputVariant="floating"
                                        disabled
                                        className='newProducerInputGroup'
                                    />
                                ) : (
                                    <InputGroup
                                        type="text"
                                        label="Producer"
                                        name="producer"
                                        value={modifiedProducer || form.nextId || " "}
                                        handleChange={handleChange}
                                        inputVariant="floating"
                                        disabled
                                        className='newProducerInputGroup'
                                    />
                                )}
                                </div>

                                {/* Producer Fields - Conditionally Rendered */}
                                {!shouldHideProducerFields && (
                                    <div className="grid grid-cols-1 gap-3">
                                        {/* <InputGroup
                                            type="text"
                                            label="Producer name"
                                            name="producerName"
                                            value={form.producerName}
                                            handleChange={handleChange}
                                            inputVariant="floating"
                                        /> */}
                                        <InputGroup
                                            type="text"
                                            label="Producer Show"
                                            name="producerShow"
                                            value={form.producerShow}
                                            handleChange={handleChange}
                                            inputVariant="floating"
                                            className='newProducerInputGroup'
                                        />
                                    </div>
                                )}

                                {/* Label Name and Noise Mode */}
                                <div className="grid grid-cols-2 gap-3">
                                    {/* Label Name or Clickable Words */}
                                    <div className="flex flex-col" ref={labelInputWrapperRef}>
                                        {!form.noiseMode ? (
                                            <div className="relative w-full mb-8">
                                                <Input
                                                    type="text"
                                                    name="labelName"
                                                    value={form.labelName}
                                                    onChange={handleChange}
                                                    onFocus={() => {
                                                        if (labelBlurTimeoutRef.current) {
                                                            clearTimeout(labelBlurTimeoutRef.current);
                                                            labelBlurTimeoutRef.current = null;
                                                        }
                                                        setIsLabelFieldFocused(true);
                                                    }}
                                                    onBlur={() => {
                                                        labelBlurTimeoutRef.current = setTimeout(() => {
                                                            setIsLabelFieldFocused(false);
                                                        }, 150);
                                                    }}
                                                    className={`newProducerLabelName h-14 px-6 rounded-l-full rounded-r-full border border-gray-300 bg-background ring-offset-background hover:border-gray-400 ${isLabelFieldFocused ? 'border-blue-500 ring-2 ring-blue-200' : ''}`}
                                                    placeholder=" "
                                                />
                                                <label
                                                    htmlFor="labelName"
                                                    className={`absolute left-5 text-gray-500 transition-all duration-200 pointer-events-none
                                                        ${(form.labelName || isLabelFieldFocused) ? 'top-0 -translate-y-1/2 text-xs font-medium bg-white px-2 ml-2 scale-85' : 'top-1/2 -translate-y-1/2'}
                                                        ${isLabelFieldFocused ? '!text-blue-600' : ''}
                                                    `}
                                                >
                                                    Label Name
                                                </label>
                                            </div>
                                        ) : (
                                            <div className="relative w-full mb-8">
                                                <div
                                                    className="newProducerLabelNameNoise flex w-auto min-w-0 rounded-l-full rounded-r-full border border-input bg-background px-6 py-2 gap-1 text-sm ring-offset-background flex-wrap items-center min-h-[2.5rem] whitespace-normal break-words focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                                                    style={{ width: '100%', wordBreak: 'break-word', whiteSpace: 'normal' }}
                                                >
                                                    {form.labelName
                                                        .split(/\s+/)
                                                        .filter(Boolean)
                                                        .map((word, index) => (
                                                            <span
                                                                key={`${word}-${index}`}
                                                                onClick={() => handleLabelWordClick(word)}
                                                                className="cursor-pointer transition-all duration-200 rounded break-words whitespace-normal"
                                                            >
                                                                {word}
                                                            </span>
                                                        ))}
                                                </div>
                                                <label
                                                    htmlFor="labelName"
                                                    className={`absolute left-5 text-gray-500 transition-all duration-200 pointer-events-none
                                                        ${form.labelName ? 'top-0 -translate-y-1/2 text-xs font-medium bg-white px-2 ml-2 scale-85' : 'top-1/2 -translate-y-1/2'}
                                                        peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs peer-focus:font-medium
                                                        peer-focus:bg-white peer-focus:px-2 peer-focus:ml-2 peer-focus:scale-85
                                                        peer-focus:text-blue-600
                                                    `}
                                                >
                                                    Label Name
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                    {/* Noise Mode Checkbox */}
                                    <div className="flex items-center justify-center h-full">
                                        <input
                                            className="red-checkbox"
                                            type="checkbox"
                                            name="noiseMode"
                                            checked={form.noiseMode}
                                            onChange={handleChange}
                                        />
                                        <label className="text-sm font-medium text-gray-700 ml-2">Noise Mode</label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 -translate-y-4.5">
                                    <CreatableMultiSelectInputGroup
                                        label="Noise"
                                        name="noises"
                                        value={form.noises}
                                        options={form.noiseMode ? [] : (() => {
                                            if (noiseEnabled && form.labelName) {
                                                const seen = new Set();
                                                return form.labelName
                                                    .split(/\s+/)
                                                    .filter(Boolean)
                                                    .filter(word => {
                                                        if (seen.has(word)) return false;
                                                        seen.add(word);
                                                        return !form.noises.includes(word);
                                                    })
                                                    .map(word => ({ value: word, label: word }));
                                            }
                                            return [];
                                        })()}
                                        handleChange={(values: string[]) => handleMultiSelectChange('noises', values)}
                                        className="mb-3"
                                    />
                                </div>

                                {/* Wine Properties */}
                                <div className="grid grid-cols-2 gap-3">
                                <SearchableFloatingSelect
                                    label="Variety"
                                    name="variety"
                                    value={form.variety ? {
                                    label: varietiesOptions.find(v => String(v.value) === String(form.variety))?.label || '',
                                    value: String(form.variety)
                                    } : null}
                                    options={varietiesOptions}
                                    onChange={(option) => {
                                    const newVariety = option ? parseInt(option.value) : 0;
                                    
                                    let updatedForm = {
                                        ...form,
                                        variety: newVariety
                                    };

                                    // NEW REQUIREMENT: Handle clearing variety
                                    if (newVariety === 0 || !option) {
                                        // Reset variety-related state completely
                                        updatedForm.variety = 0;
                                        updatedForm.color = ''; // Reset color when variety is cleared
                                        
                                        // Clear ALL variety-specific global nots completely
                                        let globalNots = updatedForm.globalNotKeyword || '';
                                        
                                        // Remove !reserve temporarily to ensure it goes to the end
                                        globalNots = globalNots.replace(/&?!reserve/g, '');
                                        
                                        // Remove ALL variety-specific substitutions from global nots
                                        Object.values(colorNotSubstitutions).forEach(substitution => {
                                            if (substitution) {
                                                const subTerms = substitution.split('&').filter(Boolean);
                                                subTerms.forEach(term => {
                                                    const termPattern = new RegExp(`&?${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
                                                    globalNots = globalNots.replace(termPattern, '');
                                                });
                                            }
                                        });
                                        
                                        // Clean up any double ampersands and leading/trailing ampersands
                                        globalNots = globalNots.replace(/&{2,}/g, '&').replace(/^&+|&+$/g, '');
                                        
                                        // Always add !reserve at the end
                                        globalNots = globalNots ? `${globalNots}&!reserve` : '!reserve';
                                        
                                        updatedForm.globalNotKeyword = globalNots;
                                        
                                        // If not in edit mode, regenerate keywords without variety logic
                                        if (!editingWine) {
                                            updatedForm = generateKeywords(updatedForm, keywordWeights);
                                            // Don't apply variety-specific logic when variety is cleared
                                        }
                                        setForm(updatedForm);
                                        return;
                                    }

                                    // In non-edit mode, regenerate keywords when variety changes
                                    if (!editingWine) {
                                        
                                        // Reset globalNotKeyword to clean state before generating new keywords
                                        updatedForm.globalNotKeyword = '!reserve';
                                        
                                        // Always generate basic keywords first
                                        updatedForm = generateKeywords(updatedForm, keywordWeights);
                                        
                                        // Check if transformation data is loaded for variety-specific logic
                                        if (isTransformationDataLoaded && varietiesFullData.length > 0 && colorsOptions.length > 0 && newVariety !== 0) {
                                            updatedForm = applyNewGenerateKeywordLogic(updatedForm);
                                            // Re-run generateKeywords to properly manage !reserve after color substitutions
                                            updatedForm = generateKeywords(updatedForm, keywordWeights);
                                        }
                                    }
                                    setForm(updatedForm);
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                />
                                <SearchableFloatingSelect
                                    label="Color"
                                    name="color"
                                    value={form.color ? {
                                    label: colorsOptions.find(c => String(c.value) === String(form.color))?.label || '',
                                    value: String(form.color)
                                    } : null}
                                    options={colorsOptions}
                                    onChange={(option) => {
                                    
                                    let updatedForm = {
                                        ...form,
                                        color: option ? option.value : ''
                                    };

                                    // If color is being cleared (option is null), clear the form data
                                    if (!option) {
                                        // Clear color-related data when color is cleared
                                        updatedForm = {
                                            ...updatedForm,
                                            color: '',
                                            // Reset to basic state when color is cleared
                                            globalNotKeyword: '!reserve'
                                        };
                                        
                                        // Regenerate keywords without color-specific logic
                                        if (!editingWine) {
                                            updatedForm = generateKeywords(updatedForm, keywordWeights);
                                        }
                                    } else {
                                        // If not in edit mode and transformation data is loaded, trigger keyword generation
                                        if (!editingWine && isTransformationDataLoaded && varietiesFullData.length > 0 && colorsOptions.length > 0) {
                                            
                                            // Always generate basic keywords first
                                            updatedForm = generateKeywords(updatedForm, keywordWeights);
                                            
                                            // Check if we should apply variety-specific transformations based on the selected color
                                            if (option && option.value) {
                                                
                                                // Create a modified form that will trigger new keyword generation logic
                                                // We'll use the selected color directly for keyword generation
                                                const modifiedFormForColorLogic = {
                                                    ...updatedForm,
                                                    // Keep the selected color for keyword generation
                                                };
                                                
                                                // Apply new generateKeyword logic which will now use the color dropdown data
                                                updatedForm = applyNewGenerateKeywordLogic(modifiedFormForColorLogic);
                                                // Re-run generateKeywords to properly manage !reserve after color substitutions
                                                updatedForm = generateKeywords(updatedForm, keywordWeights);
                                            }
                                        }
                                    }
                                    
                                    setForm(updatedForm);
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                <SearchableFloatingSelect
                                    label="Sweetness"
                                    name="sweetness"
                                    value={form.sweetness ? {
                                    label: sweetnessOptions.find(s => String(s.value) === String(form.sweetness))?.label || '',
                                    value: String(form.sweetness)
                                    } : null}
                                    options={sweetnessOptions}
                                    onChange={(option) => {
                                    setForm(prev => ({
                                        ...prev,
                                        sweetness: option ? option.value : ''
                                    }));
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                />
                                <SearchableFloatingSelect
                                    label="Type"
                                    name="wineType"
                                    value={form.wineType ? {
                                    label: wineTypesOptions.find(t => String(t.value) === String(form.wineType))?.label || '',
                                    value: String(form.wineType)
                                    } : null}
                                    options={wineTypesOptions}
                                    onChange={(option) => {
                                    setForm(prev => ({
                                        ...prev,
                                        wineType: option ? option.value : ''
                                    }));
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                />
                                </div>

                                {/* Location Fields */}
                                <div className="grid grid-cols-2 gap-3">
                                <SearchableFloatingSelect
                                    label="Country"
                                    name="country"
                                    value={form.country ? {
                                        label: countryOptions.find(c => String(c.value) === String(form.country))?.label || '',
                                        value: String(form.country)
                                    } : null}
                                    options={countryOptions}
                                    onChange={(option) => {
                                        const countryId = option ? parseInt(option.value) : 0;
                                        setForm(prev => ({
                                        ...prev,
                                        country: countryId,
                                        // Reset dependent fields when country changes
                                        region: 0,
                                        sub_region: 0
                                        }));
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                />
                                <SearchableFloatingSelect
                                    label="Region"
                                    name="region"
                                    value={form.region ? {
                                        label: regionOptions.find(r => String(r.value) === String(form.region))?.label || '',
                                        value: String(form.region)
                                    } : null}
                                    options={regionOptions}
                                    onChange={(option) => {
                                        const regionId = option ? parseInt(option.value) : 0;
                                        setForm(prev => ({
                                        ...prev,
                                        region: regionId,
                                        // Reset dependent fields
                                        sub_region: 0
                                        }));
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                               <SearchableFloatingSelect
                                    label="Sub-Region"
                                    name="sub_region"
                                    value={form.sub_region ? {
                                        label: subRegionOptions.find(sr => String(sr.value) === String(form.sub_region))?.label || '',
                                        value: String(form.sub_region)
                                    } : null}
                                    options={subRegionOptions}
                                    onChange={(option) => {
                                        setForm(prev => ({
                                        ...prev,
                                        sub_region: option ? parseInt(option.value) : 0
                                        }))
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                />
                                <SearchableFloatingSelect
                                    label="Appellation"
                                    name="appellations"
                                    value={form.appellations ? {
                                        label: appellationsOptions.find(a => String(a.value) === String(form.appellations))?.label || '',
                                        value: String(form.appellations)
                                    } : null}
                                    options={appellationsOptions}
                                    onChange={(option) => {
                                        setForm(prev => ({
                                        ...prev,
                                        appellations: option ? parseInt(option.value) : 0
                                        }))
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                />
                                </div>

                                {/* Sub-Appellation Field */}
                                <div className="grid grid-cols-1 gap-3">
                                    <SearchableFloatingSelect
                                    label="Sub-Appellation"
                                    name="sub_appellations"
                                    value={form.sub_appellations ? {
                                        label: subAppellationsOptions.find(sa => String(sa.value) === String(form.sub_appellations))?.label || '',
                                        value: String(form.sub_appellations)
                                    } : null}
                                    options={subAppellationsOptions}
                                    onChange={(option) => {
                                        setForm(prev => ({
                                        ...prev,
                                        sub_appellations: option ? parseInt(option.value) : 0
                                        }))
                                    }}
                                    error=""
                                    className='searchableFloatingSelect'
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                   <SearchableFloatingSelect
                                        label="All Appellations Tree"
                                        name="all_appellations_tree"
                                        value={form.all_appellations_tree ? {
                                            label: appellationTreeOptions.find(a => String(a.value) === String(form.all_appellations_tree))?.label || '',
                                            value: String(form.all_appellations_tree)
                                        } : null}
                                        options={appellationTreeOptions}
                                        onChange={(option) => {
                                            // Update the all_appellations_tree field
                                            setForm(prev => ({
                                                ...prev,
                                                all_appellations_tree: option ? option.value : ''
                                            }));
                                            
                                            // Find the full appellation tree object that matches the selected value
                                            if (option && option.value) {
                                                const selectedAppellationTree = appellationTree?.find((item: any) => 
                                                    item.name === option.value
                                                );
                                                if (selectedAppellationTree) {
                                                    handleAppellationTreeSelect(selectedAppellationTree);
                                                }
                                            }
                                        }}
                                        error=""
                                        className='searchableFloatingSelect'
                                    />
                                </div>

                                {/* Producer Noise */}
                                <CreatableMultiSelectInputGroup
                                    label="Producer Noise"
                                    name="producerNoises"
                                    value={form.producerNoises}
                                    handleChange={(values: string[]) => handleMultiSelectChange('producerNoises', values)}
                                    className="mt-2"
                                    disabled
                                    
                                />
                            </div>

                            

                            {/* Right Column */}
                            <div className="space-y-4 lg:border-l lg:border-gray-200 lg:pl-6">
                                {/* Existing Producer Wines Table - Only show when hiding producer fields */}
                                {((shouldHideProducerFields && literalOfProducer) || editingWine) && (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center ">
                                                <span className="font-medium text-gray-700">Existing Producer Wines</span>
                                                {/* Count badge for wines */}
                                                <Badge count={wineTableData.length === 0 ? 0 : wineTableData.length} />
                                            </div>
                                            <Button
                                                variant='dark'
                                                size='md'
                                                onClick={handleMergeSelected}
                                                className={
                                                    selectedWineRows.length < 1 || selectedWineRows.length > 3
                                                        ? 'bg-gray-300 !text-black opacity-50 cursor-not-allowed'
                                                        : ''
                                                }
                                                disabled={selectedWineRows.length < 1 || selectedWineRows.length > 3}
                                            >
                                                <div className="flex items-center ">
                                                    <Merge className="w-4 h-4" />
                                                    <span>Merge</span>
                                                </div>
                                            </Button>
                                        </div>
                                        <div id="wine-infinite-table" className='max-h-[350px] border border-gray-200 rounded-md bg-white overflow-auto' ref={wineTableRef}>
                                            <WineInfiniteTable
                                                data={wineTableData}
                                                hasMore={hasMoreWineTableData}
                                                isLoading={isLoadingWineTableData}
                                                isAppending={isLoadingMoreWineTableData}
                                                onLoadMore={handleLoadMoreWineTableData}
                                                onRowSelect={handleWineRowSelect}
                                                selectedWineRows={selectedWineRows}
                                                handleWordClick={handleLabelWordClick}
                                                onWineNameClick={handleWineNameClick}
                                                noiseEnabled={noiseEnabled}
                                                shouldShowEditWines={false}
                                                className=""
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Keyword Fields */}
                                <div className="flex flex-col sm:flex-row gap-4 mt-10 items-stretch">
                                    <div className="flex flex-1 items-center  min-w-0">
                                        <InputGroup
                                            type='text'
                                            label="Producer Keyword"
                                            name="producerKeyword"
                                            value={form.producerKeyword}
                                            handleChange={handleChange}
                                            inputVariant='floating'
                                            className="newProducerInputGroupWithCounter whitespace-normal break-all flex-1 min-w-0"
                                        />
                                        <TooltipButton tooltip='Add Weightage'>
                                            <Counter
                                                value={keywordWeights.producerKeyword}
                                                onChange={(value) => handleWeightChange('producerKeyword', value)}
                                            />
                                        </TooltipButton>
                                    </div>
                                    { editingWine && 
                                        <div className="flex items-stretch sm:items-start sm:justify-end w-full sm:w-auto">
                                            <Button
                                                type="button"
                                                variant='dark'
                                                size='md'
                                                onClick={handleRegenerateKeywords}
                                                className="regenerateButton w-full sm:w-auto text-sm h-[50px]"
                                            >
                                                Regenerate
                                            </Button>
                                        </div>
                                    }
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div className="flex items-center">
                                        <div className="flex-1">
                                            <InputGroup
                                                type="text"
                                                label="Base Keyword"
                                                name="baseKeyword"
                                                value={form.baseKeyword}
                                                handleChange={handleChange}
                                                inputVariant="floating"
                                                className="newProducerInputGroupWithCounter whitespace-normal break-all"
                                            />
                                        </div>
                                        <TooltipButton tooltip='Add Weightage'>
                                            <Counter
                                                value={keywordWeights.baseKeyword}
                                                onChange={(value) => handleWeightChange('baseKeyword', value)}
                                            />
                                        </TooltipButton>
                                    </div>
                                    <div className="flex items-center ">
                                        <div className="flex-1">
                                            <InputGroup
                                                type="text"
                                                label="Appellation Keyword"
                                                name="appellationKeyword"
                                                value={form.appellationKeyword}
                                                handleChange={handleChange}
                                                inputVariant="floating"
                                                className='newProducerInputGroupWithCounter'
                                            />
                                        </div>
                                        <TooltipButton tooltip='Add Weightage'>
                                            <Counter
                                                value={keywordWeights.appellationKeyword}
                                                onChange={(value) => handleWeightChange('appellationKeyword', value)}
                                            />
                                        </TooltipButton>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div className="flex items-center ">
                                        <div className="flex-1">
                                            <InputGroup
                                                type="text"
                                                label="Color Keyword"
                                                name="colorKeyword"
                                                value={form.colorKeyword}
                                                handleChange={handleChange}
                                                inputVariant="floating"
                                                className='newProducerInputGroupWithCounter'
                                            />
                                        </div>
                                        <TooltipButton tooltip='Add Weightage'>
                                            <Counter
                                                value={keywordWeights.colorKeyword}
                                                onChange={(value) => handleWeightChange('colorKeyword', value)}
                                            />
                                        </TooltipButton>
                                    </div>
                                    <div className="flex items-center ">
                                        <div className="flex-1">
                                            <InputGroup
                                                type="text"
                                                label="Common Modifiers"
                                                name="commonModifiersKeyword"
                                                value={form.commonModifiersKeyword}
                                                handleChange={handleChange}
                                                inputVariant="floating"
                                                className='newProducerInputGroupWithCounter'
                                            />
                                        </div>
                                        <TooltipButton tooltip='Add Weightage'>
                                            <Counter
                                                value={keywordWeights.commonModifiersKeyword}
                                                onChange={(value) => handleWeightChange('commonModifiersKeyword', value)}
                                            />
                                        </TooltipButton>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div className="flex items-center ">
                                        <div className="flex-1">
                                            <InputGroup
                                                type="text"
                                                label="Modifiers"
                                                name="modifiersKeyword"
                                                value={form.modifiersKeyword}
                                                handleChange={handleChange}
                                                inputVariant="floating"
                                                className='newProducerInputGroupWithCounter'
                                            />
                                        </div>
                                        <TooltipButton tooltip='Add Weightage'>
                                            <Counter
                                                value={keywordWeights.modifiersKeyword}
                                                onChange={(value) => handleWeightChange('modifiersKeyword', value)}
                                            />
                                        </TooltipButton>
                                    </div>
                                    <div className="flex-1 flex items-center">
                                        <div className="w-full">
                                            <InputGroup
                                                type="text"
                                                label="Custom Nots"
                                                name="customNotKeyword"
                                                value={form.customNotKeyword}
                                                handleChange={handleChange}
                                                inputVariant="floating"
                                                className='newProducerInputGroup'
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1">
                                    <InputGroup
                                        type="text"
                                        label="Global Nots"
                                        name="globalNotKeyword"
                                        value={form.globalNotKeyword}
                                        handleChange={handleChange}
                                        inputVariant="floating"
                                        className='newProducerInputGroup'
                                    />
                                </div>  

                                <InputGroup
                                    type="textarea"
                                    label="Keyword"
                                    name="keyword"
                                    value={form.keyword || ''}
                                    handleChange={handleChange}
                                    inputVariant="floating"
                                    className='newProducerInputGroup'
                                    disabled // Make the keyword field read-only
                                />
                                {/* Keyword Difference textarea and visual diff in edit mode */}
                                {editingWine && showKeywordDiff && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Keyword Difference:
                                        </label>
                                        <div className="border rounded-md p-3 bg-gray-50">
                                            {(() => {
                                                if (!originalKeyword && !regeneratedKeyword) {
                                                    return <span className="text-gray-500 italic">No keywords to compare.</span>;
                                                }

                                                if (originalKeyword === regeneratedKeyword) {
                                                    return <span className="text-gray-500 italic">No changes in keywords.</span>;
                                                }

                                                // Improved diff algorithm that finds common subsequences
                                                const createAdvancedDiff = (original: string, regenerated: string) => {
                                                    // Simple Myers diff algorithm implementation
                                                    const diff = [];
                                                    let i = 0, j = 0;

                                                    while (i < original.length || j < regenerated.length) {
                                                        // Find the next common substring
                                                        let commonStart = -1;
                                                        let commonLength = 0;
                                                        let bestI = i, bestJ = j;

                                                        // Look for common substrings starting from current positions
                                                        for (let x = i; x < original.length; x++) {
                                                            for (let y = j; y < regenerated.length; y++) {
                                                                let len = 0;
                                                                while (
                                                                    x + len < original.length &&
                                                                    y + len < regenerated.length &&
                                                                    original[x + len] === regenerated[y + len]
                                                                ) {
                                                                    len++;
                                                                }

                                                                // Only consider meaningful common substrings (length >= 3)
                                                                if (len >= 3 && len > commonLength) {
                                                                    commonLength = len;
                                                                    commonStart = x;
                                                                    bestI = x;
                                                                    bestJ = y;
                                                                }
                                                            }
                                                        }

                                                        if (commonLength > 0) {
                                                            // Add removed part (from i to commonStart)
                                                            if (i < commonStart) {
                                                                diff.push({
                                                                    type: 'removed',
                                                                    content: original.substring(i, commonStart)
                                                                });
                                                            }

                                                            // Add added part (from j to bestJ)
                                                            if (j < bestJ) {
                                                                diff.push({
                                                                    type: 'added',
                                                                    content: regenerated.substring(j, bestJ)
                                                                });
                                                            }

                                                            // Add common part
                                                            diff.push({
                                                                type: 'common',
                                                                content: original.substring(commonStart, commonStart + commonLength)
                                                            });

                                                            i = commonStart + commonLength;
                                                            j = bestJ + commonLength;
                                                        } else {
                                                            // No more common substrings found
                                                            // Add remaining as removed and added
                                                            if (i < original.length) {
                                                                diff.push({
                                                                    type: 'removed',
                                                                    content: original.substring(i)
                                                                });
                                                            }
                                                            if (j < regenerated.length) {
                                                                diff.push({
                                                                    type: 'added',
                                                                    content: regenerated.substring(j)
                                                                });
                                                            }
                                                            break;
                                                        }
                                                    }

                                                    // Convert diff to JSX elements
                                                    const elements: JSX.Element[] = [];
                                                    let elementIndex = 0;

                                                    // Inside createAdvancedDiff function, replace the diff.forEach block with:
                                                    diff.forEach((part) => {
                                                        if (part.content) {
                                                            switch (part.type) {
                                                                case 'added':
                                                                    elements.push(
                                                                        <span
                                                                            key={`added-${elementIndex++}`}
                                                                            className="bg-green-100 text-green-800 px-1 rounded-sm"
                                                                            title="Added"
                                                                        >
                                                                            {part.content}
                                                                        </span>
                                                                    );
                                                                    break;
                                                                case 'removed':
                                                                    elements.push(
                                                                        <span
                                                                            key={`removed-${elementIndex++}`}
                                                                            className="bg-red-100 text-red-800 px-1 rounded-sm"
                                                                            title="Removed"
                                                                        >
                                                                            {part.content}
                                                                        </span>
                                                                    );
                                                                    break;
                                                                case 'common':
                                                                    elements.push(
                                                                        <span
                                                                            key={`common-${elementIndex++}`}
                                                                            className=""
                                                                        >
                                                                            {part.content}
                                                                        </span>
                                                                    );
                                                                    break;
                                                                default:
                                                                    elements.push(
                                                                        <span key={`other-${elementIndex++}`}>{part.content}</span>
                                                                    );
                                                            }
                                                        }
                                                    });
                                                    return elements;
                                                };

                                                const diffElements = createAdvancedDiff(originalKeyword, regeneratedKeyword);

                                                if (diffElements.length === 0) {
                                                    return <span className="text-gray-500 italic">No changes detected.</span>;
                                                }

                                                return (
                                                    <div className="space-y-2">
                                                        <div className="text-xs text-gray-600 mb-2">
                                                            <span className="inline-flex items-center mr-4">
                                                                <span className="w-3 h-3 bg-red-100 border border-red-200 rounded-sm mr-1"></span>
                                                                Removed
                                                            </span>
                                                            <span className="inline-flex items-center">
                                                                <span className="w-3 h-3 bg-green-100 border border-green-200 rounded-sm mr-1"></span>
                                                                Added
                                                            </span>
                                                        </div>
                                                        <div className="font-mono text-sm leading-relaxed break-words">
                                                            {diffElements}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                    {/* Form Footer */}
                    <div className="py-4 flex justify-end gap-3">
                        <Button 
                            type="button" 
                            variant='negative'
                            size='md'
                            onClick={() => navigateBackToValidation()}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            variant='dark'
                            size='md'
                            disabled={submitting}
                        >
                            {submitting ? 
                                (shouldHideProducerFields ? 'Adding Wine...' : 'Creating...') : 
                                (editingWine ? 'Update' : (shouldHideProducerFields ? 'Add Wine' : 'Create'))
                            }
                        </Button>
                    </div>
                </form>
                )}
            </div>

            {/* Modals */}
            {showMergeModal && (
            <MergeWinesModal
                isOpen={showMergeModal}
                onClose={() => setShowMergeModal(false)}
                selectedWines={mergeModalWines}
            />
            )}
        </div>
    );
};

export default NewProducer;
